import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "en" | "de";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translate: (key: string) => string;
}

const translations = {
  en: {
    "home.hero.title": "Create Magical Gift Moments",
    "home.hero.subtitle": "Share your wishes, discover perfect gifts, and make every occasion special",
    "button.signup": "Sign Up",
    "button.signin": "Sign In",
    "language.switch.to": "Switch to German",
    "home.cta.title": "Ready to Make Gift-Giving Magical?",
    "home.cta.subtitle": "Join and start creating your wishlists today",
    "home.feature.wishlist.title": "Smart Wishlists",
    "home.feature.wishlist.description": "Create unlimited wishlists, organize gifts into sections, and share them with friends and family so everyone knows exactly what you'd love.",
    "home.feature.ai.title": "AI Gift Assistant",
    "home.feature.ai.description": "Describe the person and the occasion, and our AI Gift Assistant suggests thoughtful, personalized gift ideas in seconds.",
    "home.feature.social.title": "Social Gifting",
    "home.feature.social.description": "Connect with friends, follow their wishlists, and get reminders so you never miss a birthday or special occasion.",
    "home.feature.wanted.title": "Give what's wanted",
    "home.feature.wanted.description": "Friends reserve the gifts they'll buy, so presents are never duplicated and every gift is something the recipient truly wants.",
    "home.feature.price.title": "Price Drop Alerts",
    "home.feature.price.description": "Track prices automatically and get alerts the moment items on your wishlist — or your friends' — drop in price.",
    "home.feature.recommendation.title": "Smart Recommendation",
    "home.feature.recommendation.description": "Discover gifts tailored to each person's unique taste",
    "home.feature.Randomizer.title": "Randomizer",
    "home.feature.Randomizer.description": "Create events, manage participants, and let our system randomly assign who gives to whom!",
    "home.feature.eventmanagement.title": "Event Management",
    "home.feature.eventmanagement.description": "Plan events end to end — invite guests, track RSVPs, assign tasks and seating, and keep every detail in one place.",
    "home.section.why": "Why People Love Our Platform",
    "home.create.wishlist.nologin": "Create Wishlist Without Login",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.faq": "FAQ",
    "nav.privacy": "Privacy Policy",
    "nav.terms": "Terms of Service",
    "nav.impressum": "Impressum",
    "login.welcome": "Welcome Back",
    "login.continue.email": "Or continue with email",
    "login.email": "Email",
    "login.password": "Password",
    "login.forgot": "Forgot password?",
    "login.no.account": "Don't have an account?",
    "login.register": "Register",
    "register.create": "Create Account",
    "register.or.email": "Or register with email",
    "register.have.account": "Already have an account?",
    "forgot.title": "Forgot Password?",
    "forgot.description": "Enter your email and we'll send you a password reset link",
    "forgot.email.placeholder": "Enter your email",
    "forgot.sending": "Sending...",
    "forgot.send": "Send Reset Link",
    "forgot.back": "Back to Login",
    "footer.copyright": "© {year} BuyForS. All rights reserved.",
    "wishlist.create.title": "Create Your Wishlist",
    "wishlist.create.subtitle": "Add items to your wishlist and share them with friends and family.",
    "wishlist.create.add": "Add Wishlist Item",
    "wishlist.create.email": "Your Email",
    "wishlist.create.email.placeholder": "your.email@example.com",
    "wishlist.create.important": "Important",
    "wishlist.create.email.note": "Providing an email is required to edit your wishlist later. Without an email, you'll only receive a share link and won't be able to add or edit items.",
    "wishlist.create.create": "Create Wishlist",
    "wishlist.create.empty": "Your wishlist is empty. Add some items!",
    "wishlist.create.account": "Create an Account",
    "wishlist.create.account.desc": "Sign up to save your wishlist and access more features!",
    "wishlist.create.signup": "Sign up now",
    "wishlist.create.ready": "Your Wishlist is Ready!",
    "wishlist.create.shareurl": "Share URL:",
    "wishlist.create.editurl": "Edit URL:",
    "wishlist.create.copy.share": "Copy Share Link",
    "wishlist.create.copy.edit": "Copy Edit Link",
    "wishlist.create.no.edit": "No edit link is available because you didn't provide an email. You won't be able to edit this wishlist later.",
    "wishlist.create.sent": "Links have been sent to",
    "wishlist.create.home": "Home",
    "wishlist.item.view": "View product",
    "wishlist.item.view.link": "View product link",
    "wishlist.create.email.invalid": "Please enter a valid email",
    "wishlist.create.creating": "Creating...",
    "wishlist.create.items": "items",
    "wishlist.create.email.sent.title": "Email sent",
    "wishlist.create.email.sent": "A link to your wishlist has been sent to {email}",
    "wishlist.create.error.title": "Failed to create wishlist",
    "wishlist.create.error.description": "Please try again.",

    "public.Randomizer.title": "Play Randomizer",
    "public.Randomizer.subtitle": "Create and draw names instantly - no registration required!",
    "public.Randomizer.home": "Home",
    "public.Randomizer.game.name": "Game Name",
    "public.Randomizer.game.name.placeholder": "e.g., Family Randomizer 2023",
    "public.Randomizer.participants": "Participants",
    "public.Randomizer.add.participant": "Add Participant",
    "public.Randomizer.email": "Email",
    "public.Randomizer.name": "Name",
    "public.Randomizer.name.placeholder": "Full Name",
    "public.Randomizer.min.participants": "Minimum 3 participants required",
    "public.Randomizer.budget.max": "Maximum Budget",
    "public.Randomizer.optional": "Optional",
    "public.Randomizer.exclusions": "Exclusions",
    "public.Randomizer.add.exclusion": "Add Exclusion",
    "public.Randomizer.exclusion.participant": "Participant",
    "public.Randomizer.cannot.match": "cannot match",
    "public.Randomizer.exclusion.excluded": "Excluded Participant",
    "public.Randomizer.exclusions.description": "Set rules to prevent certain participants from being matched with each other. Exclusions are bidirectional - if you set 'A cannot match B', then A won't be assigned to B AND B won't be assigned to A.",
    "public.Randomizer.draw": "Draw Names",
    "public.Randomizer.drawing": "Drawing...",
    "public.Randomizer.success.title": "Randomizer Draw Complete!",
    "public.Randomizer.success.description": "Emails have been sent to {count} participants with their assignments.",
    "public.Randomizer.success.check.email": "Each participant will receive an email with the name of the person they should buy a gift for.",
    "public.Randomizer.create.another": "Create Another",
    "public.Randomizer.go.home": "Go Home",
    "public.Randomizer.error.game.name": "Please enter a game name",
    "public.Randomizer.error.min.participants": "At least 3 participants are required",
    "public.Randomizer.error.invalid.email": "Invalid email: {email}",
    "public.Randomizer.error.duplicate.emails": "Duplicate email addresses are not allowed",
    "public.Randomizer.error.exclusion.not.found": "Participant not found: {email}",
    "public.Randomizer.error.exclusion.self": "A participant cannot exclude themselves",
    "public.Randomizer.error.draw": "Failed to create Randomizer game",
    "public.Randomizer.error.draw.description": "Please check your inputs and try again.",

    "wishlist.edit.title": "Edit Your Wishlist",
    "wishlist.edit.subtitle": "Update or remove items from your wishlist.",
    "wishlist.edit.empty": "Your wishlist is empty.",
    "wishlist.edit.delete": "Delete Wishlist",
    "wishlist.edit.delete.confirm": "Are you absolutely sure?",
    "wishlist.edit.delete.desc": "This will permanently delete your wishlist and all its items. This action cannot be undone.",
    "wishlist.edit.delete.cancel": "Cancel",
    "wishlist.edit.delete.confirm.btn": "Delete Wishlist",
    "wishlist.edit.deleting": "Deleting...",
    "wishlist.edit.verify.title": "Verify Your Email",
    "wishlist.edit.verify.subtitle": "To edit your wishlist, please enter the email you used when creating it.",
    "wishlist.edit.verify.email": "Email",
    "wishlist.edit.verify.send": "Send Verification Token",
    "wishlist.edit.verify.sending": "Sending...",
    "wishlist.edit.verify.token": "Verification Token",
    "wishlist.edit.verify.token.placeholder": "Paste your verification token here",
    "wishlist.edit.verify.token.btn": "Verify Token",
    "wishlist.edit.verify.verifying": "Verifying...",

    "wishlist.share.title": "Wishlist",
    "wishlist.share.subtitle": "Here are the items someone would love to receive!",
    "wishlist.share.empty": "This wishlist is empty.",
    "wishlist.share.create": "Create your own wishlist with",

    // SimpleWishlistDialog translations
    "wishlist.item.add": "Add Wishlist Item",
    "wishlist.item.edit": "Edit Wishlist Item",
    "wishlist.item.name": "Item Name",
    "wishlist.item.name.placeholder": "Enter item name",
    "wishlist.item.description": "Description",
    "wishlist.item.description.placeholder": "Optional description",
    "wishlist.item.link": "Product Link",
    "wishlist.item.link.placeholder": "https://example.com/product",
    "wishlist.item.saving": "Saving...",
    "wishlist.item.update": "Update Item",
    "wishlist.item.add.button": "Add to Wishlist",
    "wishlist.powered.by": "Supported by",
    "wishlist.create.copy.success": "Copied!",
    "wishlist.create.copy.share.success": "Share link copied to clipboard",
    "wishlist.create.copy.edit.success": "Edit link copied to clipboard",
    "wishlist.create.copy.error": "Copy Failed",
    "wishlist.create.copy.error.desc": "Failed to copy link to clipboard",

    // Added error messages
    "wishlist.item.removed": "Item removed",
    "wishlist.item.removed.desc": "The wishlist item has been removed",
    "wishlist.error.load": "Error loading wishlist",
    "wishlist.error.load.invalid": "Could not load this wishlist. It may have been removed or the link is invalid.",
    "wishlist.error.auth.expired": "Authentication expired",
    "wishlist.error.auth.expired.desc": "Please verify your email again to continue",
    "wishlist.error.update": "Error",
    "wishlist.error.update.desc": "Failed to update wishlist item",
    "wishlist.error.update.failed": "Failed to update wishlist item",
    "wishlist.error.delete": "Error",
    "wishlist.error.delete.desc": "Failed to delete wishlist item",
    "wishlist.error.delete.wishlist": "Error",
    "wishlist.error.delete.wishlist.desc": "Failed to delete wishlist. Please try again later.",
    "wishlist.error.missing.uuid": "Missing UUID",
    "wishlist.error.missing.token": "Authentication token is missing",
    "wishlist.error.verification.failed": "Failed to send verification email",
    "wishlist.error.invalid.token": "Invalid verification token",

    // Registration form fields and age validation
    "register.form.email": "Email",
    "register.form.email.placeholder": "Enter email",
    "register.form.fullName": "Full Name",
    "register.form.fullName.placeholder": "Enter Your Full Name",
    "register.form.birthday": "Date of Birth",
    "register.form.birthday.placeholder": "YYYY-MM-DD",
    "register.form.nickname": "Nickname",
    "register.form.nickname.placeholder": "Enter Nickname",
    "register.form.password": "Password",
    "register.form.password.placeholder": "Enter password",
    "register.form.phone": "Phone number (optional)",
    "register.form.phone.placeholder": "Enter phone number",
    "register.form.eligibility": "You must be at least 16 years old to register",
    "register.form.terms": "By registering, you agree to our",
    "register.form.termsLink": "Terms of Service",
    "register.form.and": "and",
    "register.form.privacyLink": "Privacy Policy",
    "register.form.aiConsent": ", including the use of AI-powered features (Google Gemini) for gift recommendations",
    "register.button.register": "Register",
    "register.button.registering": "Registering...",

    // Zod schema validation messages (RegisterForm)
    "register.form.error.email": "Invalid email format",
    "register.form.error.password": "Password must be at least 9 characters",
    "register.form.error.fullName": "Full name is required",
    "register.form.error.birthdayRequired": "Date of birth is required",
    "register.form.error.age16": "You must be at least 16 years old to use BuyForS. Under EU GDPR (Art. 8) and German law, processing of personal data of children under 16 requires parental consent. Please ask a parent or guardian to register on your behalf.",
    "register.form.error.termsAccepted": "You must accept the Terms of Service and Privacy Policy",
    "register.form.error.generic": "An unexpected error occurred during registration. Please try again.",

    // Age validation errors
    "errors.age.tooYoung": "You must be at least 16 years old to register",
    "errors.age.tooOld": "Please enter a valid birth date",

    // Landing pages translations
    "landing.nav.login": "Login",
    "landing.nav.signup": "Sign Up Free",
    "landing.footer.tagline": "The best platform for organizing Randomizer gift exchanges and managing wishlists.",
    "landing.footer.features": "Features",
    "landing.footer.support": "Support",
    "landing.footer.legal": "Legal",
    "landing.footer.copyright": "© {year} BuyForS. All rights reserved.",

    // Footer links
    "footer.link.Randomizer": "Randomizer",
    "footer.link.smartWishlists": "Smart Wishlists",
    "footer.link.aiGiftAssistant": "AI Gift Assistant",
    "footer.link.socialGifting": "Social Gifting",
    "footer.link.giveWhatsWanted": "Give What's Wanted",
    "footer.link.priceDropAlerts": "Price Drop Alerts",
    "footer.link.smartRecommendations": "Smart Recommendations",
    "footer.link.eventManagement": "Event Management",
    "footer.link.faq": "FAQ",
    "footer.link.contact": "Contact",
    "footer.link.about": "About",
    "footer.link.privacyPolicy": "Privacy Policy",
    "footer.link.termsOfService": "Terms of Service",
    "footer.link.impressum": "Impressum",
    "footer.link.chromeExtension": "Chrome Extension",
    "footer.link.status": "Status",

    // Landing page taglines
    "wishlists.footer.tagline": "The smartest platform for creating and sharing wishlists with friends and family.",
    "ai.footer.tagline": "AI-powered gift recommendations that make every occasion special.",
    "social.footer.tagline": "The social platform that makes gift-giving a joyful, connected experience.",
    "wanted.footer.tagline": "Making gift-giving a joy for both givers and recipients through wishlist-based presents.",
    "price.footer.tagline": "Smart price monitoring that saves you money on every purchase.",
    "recommendations.footer.tagline": "AI-powered gift recommendations that make every occasion special.",

    // Randomizer Landing
    "Randomizer.landing.title": "Free Randomizer Generator",
    "Randomizer.landing.subtitle": "Create and manage Randomizer gift exchanges effortlessly. Set budgets, exclude participants, send invitations, and make your holiday celebrations memorable with our free online tool.",
    "Randomizer.landing.cta.primary": "Create Free Randomizer",
    "Randomizer.landing.cta.secondary": "Sign In",
    "Randomizer.landing.features.title": "Why Choose Our Randomizer Generator?",
    "Randomizer.landing.how.title": "How It Works",
    "Randomizer.landing.faq.title": "Frequently Asked Questions",
    "Randomizer.landing.final.title": "Ready to Create Your Randomizer Event?",
    "Randomizer.landing.final.subtitle": "Join thousands of users who've made their holiday gift exchanges magical with BuyForS",
    "Randomizer.landing.final.cta": "Get Started for Free",
    "Randomizer.landing.games.title": "Popular Gift Exchange Games",
    "Randomizer.landing.games.subtitle": "Our Randomizer works perfectly for all these popular gift exchange games and more!",
    "Randomizer.landing.games.secretsanta.title": "Secret Santa",
    "Randomizer.landing.games.secretsanta.desc": "The classic holiday gift exchange where everyone receives a secret giver",
    "Randomizer.landing.games.whiteelephant.title": "White Elephant",
    "Randomizer.landing.games.whiteelephant.desc": "Fun gift swap game where participants can steal and exchange gifts",
    "Randomizer.landing.games.yankeeswap.title": "Yankee Swap",
    "Randomizer.landing.games.yankeeswap.desc": "Gift exchange with multiple rounds of swapping and trading",
    "Randomizer.landing.games.dirtysanta.title": "Dirty Santa",
    "Randomizer.landing.games.dirtysanta.desc": "Competitive gift exchange with stealing and strategic gameplay",
    "Randomizer.landing.games.kriskringle.title": "Kris Kringle",
    "Randomizer.landing.games.kriskringle.desc": "Traditional name exchange game popular in many cultures",
    "Randomizer.landing.games.pollyanna.title": "Pollyanna",
    "Randomizer.landing.games.pollyanna.desc": "Friendly gift exchange where everyone draws a name secretly",
    "Randomizer.landing.games.giftswap.title": "Gift Swap",
    "Randomizer.landing.games.giftswap.desc": "Simple and fun gift exchange perfect for any occasion",
    "Randomizer.landing.games.mysterygift.title": "Mystery Gift Exchange",
    "Randomizer.landing.games.mysterygift.desc": "Add excitement with anonymous gift giving and receiving",

    // Smart Wishlists Landing
    "wishlists.landing.title": "Smart Wishlists",
    "wishlists.landing.subtitle": "Create intelligent wishlists that adapt to your preferences. Share with friends and family, get price alerts, and never receive unwanted gifts again.",
    "wishlists.landing.cta.primary": "Create Your Smart Wishlist",
    "wishlists.landing.cta.secondary": "Try Without Account",
    "wishlists.landing.features.title": "Why Smart Wishlists?",
    "wishlists.landing.how.title": "How It Works",
    "wishlists.landing.final.title": "Ready to Create Your Smart Wishlist?",
    "wishlists.landing.final.subtitle": "Join thousands who've revolutionized their gift-giving and receiving experience",

    // AI Gift Assistant Landing
    "ai.landing.title": "AI Gift Assistant",
    "ai.landing.subtitle": "Get AI-powered gift recommendations tailored to each person's unique taste. Our intelligent assistant analyzes preferences to suggest perfect gifts every time.",
    "ai.landing.cta.primary": "Try AI Gift Assistant",
    "ai.landing.cta.secondary": "Learn More",
    "ai.landing.features.title": "Powered by Advanced AI",
    "ai.landing.how.title": "How AI Gift Assistant Works",
    "ai.landing.faq.title": "Common Questions",
    "ai.landing.final.title": "Ready to Find the Perfect Gift?",
    "ai.landing.final.subtitle": "Let AI help you become the best gift-giver in your circle",

    // Social Gifting Landing
    "social.landing.title": "Social Gifting Platform",
    "social.landing.subtitle": "Connect with friends and family to never miss important occasions. Share wishlists, coordinate group gifts, and make every celebration special.",
    "social.landing.cta.primary": "Join the Community",
    "social.landing.cta.secondary": "Explore Social Features",
    "social.landing.features.title": "Connect and Celebrate Together",
    "social.landing.how.title": "How Social Gifting Works",
    "social.landing.benefits.title": "Why Choose Social Gifting?",
    "social.landing.final.title": "Ready to Transform Your Gift-Giving?",
    "social.landing.final.subtitle": "Join our social gifting community and make every celebration unforgettable",

    // Event Management Landing
    "events.landing.title": "Plan & Host Perfect Events",
    "events.landing.subtitle": "From small gatherings to large celebrations, manage every detail with our comprehensive event platform. Track RSVPs, coordinate tasks, and create unforgettable experiences.",
    "events.landing.cta": "Start Planning Your Event",
    "events.landing.features.title": "Everything You Need to Host Amazing Events",
    "events.landing.feature.guests.title": "Guest Management",
    "events.landing.feature.guests.desc": "Send invitations, track RSVPs, manage plus-ones, and keep your guest list organized in one place.",
    "events.landing.feature.timeline.title": "Event Timeline",
    "events.landing.feature.timeline.desc": "Create detailed schedules and timelines so everyone knows what's happening and when during your event.",
    "events.landing.feature.tasks.title": "Task Coordination",
    "events.landing.feature.tasks.desc": "Assign tasks to participants, track progress, and ensure nothing falls through the cracks.",
    "events.landing.feature.seating.title": "Seating Charts",
    "events.landing.feature.seating.desc": "Plan perfect seating arrangements with our visual seating chart tool - ideal for dinners and receptions.",
    "events.landing.feature.qr.title": "QR Check-In",
    "events.landing.feature.qr.desc": "Streamline event check-in with QR codes. Track attendance and welcome guests efficiently.",
    "events.landing.feature.thanks.title": "Thank You Notes",
    "events.landing.feature.thanks.desc": "Send personalized thank you messages to guests after your event with a single click.",
    "events.landing.how.title": "How It Works",
    "events.landing.how.step1.title": "Create Your Event",
    "events.landing.how.step1.desc": "Set up your event details including date, time, location, and type. Choose visibility settings and participant limits.",
    "events.landing.how.step2.title": "Invite Guests",
    "events.landing.how.step2.desc": "Add participants from your friends list or invite via email. Send beautiful invitations and track RSVPs in real-time.",
    "events.landing.how.step3.title": "Coordinate Details",
    "events.landing.how.step3.desc": "Manage tasks, create timelines, plan seating, and upload event media - all from one dashboard.",
    "events.landing.how.step4.title": "Host & Enjoy",
    "events.landing.how.step4.desc": "Use QR check-in for seamless arrival, and after the event, send thank you notes to show your appreciation.",
    "events.landing.occasions.title": "Perfect For Any Occasion",
    "events.landing.occasions.birthdays": "Birthday Parties",
    "events.landing.occasions.weddings": "Weddings",
    "events.landing.occasions.corporate": "Corporate Events",
    "events.landing.occasions.gatherings": "Gatherings",
    "events.landing.final.title": "Ready to Plan Your Perfect Event?",
    "events.landing.final.subtitle": "Join thousands of hosts who trust BuyForS to make their events memorable and stress-free.",
    "events.landing.final.cta": "Get Started Free",

    // Give What's Wanted Landing
    "wanted.landing.title": "Give What's Wanted",
    "wanted.landing.subtitle": "Ensure your gifts are always appreciated with our wishlist-based gifting system. Give exactly what's wanted, not what you think they need.",
    "wanted.landing.cta.primary": "Start Giving Better",
    "wanted.landing.cta.secondary": "Create a Wishlist",
    "wanted.landing.problem.title": "End Gift-Giving Guesswork Forever",
    "wanted.landing.problem.subtitle": "No more returning unwanted gifts, no more awkward smiles, no more wasted money. Give gifts that truly delight every single time.",
    "wanted.landing.features.title": "How We Ensure Perfect Gifts",
    "wanted.landing.success.title": "Real Success Stories",
    "wanted.landing.final.title": "Ready to Give Perfect Gifts?",
    "wanted.landing.final.subtitle": "Join thousands who've transformed their gift-giving with wishlist-based presents",

    // Price Drop Alerts Landing
    "price.landing.title": "Price Drop Alerts",
    "price.landing.subtitle": "Never overpay again! Get instant notifications when prices drop on your wishlist items and your friends' wishlists. Save money on every purchase.",
    "price.landing.cta.primary": "Start Saving Money",
    "price.landing.cta.secondary": "Add Items to Track",
    "price.landing.savings.title": "💰 Users Save an Average of $247 per Year",
    "price.landing.features.title": "Smart Price Monitoring",
    "price.landing.how.title": "How Price Tracking Works",
    "price.landing.final.title": "Ready to Start Saving Money?",
    "price.landing.final.subtitle": "Join thousands who never overpay thanks to our smart price alerts",

    // Smart Recommendations Landing
    "recommendations.landing.title": "Smart Recommendations",
    "recommendations.landing.subtitle": "Discover gifts tailored to each person's unique taste with our advanced recommendation engine. Find the perfect present every time with AI-powered insights.",
    "recommendations.landing.cta.primary": "Get Personalized Recommendations",
    "recommendations.landing.cta.secondary": "Explore Recommendations",
    "recommendations.landing.features.title": "Powered by Advanced AI",
    "recommendations.landing.how.title": "How Smart Recommendations Work",
    "recommendations.landing.occasions.title": "Perfect for Every Occasion",
    "recommendations.landing.stats.title": "Proven Results",
    "recommendations.landing.final.title": "Ready to Discover Perfect Gifts?",
    "recommendations.landing.final.subtitle": "Let our AI help you become the gift-giver everyone remembers",

    // Card Titles and Content - Landing Pages
    "card.smart.exclusions": "Smart Exclusions",
    "card.automatic.invitations": "Automatic Invitations",
    "card.free": "100% Free",
    "card.friend.network": "Friend Network",
    "card.gift.reservations": "Gift Reservations",
    "card.smart.notifications": "Smart Notifications",
    "card.intelligent.organization": "Intelligent Organization",
    "card.price.tracking": "Price Tracking",
    "card.detailed.preferences": "Detailed Preferences",
    "card.smart.conversations": "Smart Conversations",
    "card.personalized.analysis": "Personalized Analysis",
    "card.budget.optimization": "Budget Optimization",
    "card.genuine.wants": "Genuine Wants",
    "card.no.duplicates": "No Duplicates",
    "card.priority.levels": "Priority Levels",
    "card.privacy.protection": "Privacy Protection",
    "card.detailed.info": "Detailed Info",
    "card.happy.recipients": "Happy Recipients",
    "card.instant.notifications": "Instant Notifications",
    "card.friends.wishlists": "Friends' Wishlists",
    "card.lightning.deals": "Lightning Deals",
    "card.deep.learning": "Deep Learning",
    "card.personal.preferences": "Personal Preferences",
    "card.trend.analysis": "Trend Analysis",
    "card.surprise.factor": "Surprise Factor",
    "card.social.context": "Social Context",
    "card.realtime.updates": "Real-time Updates",

    // Card Content Text
    "card.exclusions.desc": "Prevent couples, family members, or specific participants from being assigned to each other with advanced exclusion rules.",
    "card.invitations.desc": "Send email invitations automatically with Randomizer assignments. Participants receive their recipient's information securely.",
    "card.free.desc": "Our Randomizer generator is completely free to use. Create unlimited events and invite up to 100 participants.",
    "card.network.desc": "Connect with friends and family members to see their wishlists and important dates. Build your gift-giving network and never miss special occasions.",
    "card.reservations.desc": "Secretly reserve items from friends' wishlists to avoid duplicate gifts. Coordinate with others without spoiling the surprise.",
    "card.notifications.desc": "Get intelligent notifications about upcoming occasions, new wishlist items, and gift opportunities from your social network.",
    "card.organization.desc": "Automatically categorize and organize your wishlist items with smart sections and tags for easy browsing.",
    "card.tracking.desc": "Automatically track price changes on your wishlist items and get notified when they go on sale.",
    "card.preferences.desc": "Add personal notes, preferred colors, sizes, and alternative options to help others choose the perfect gift.",
    "card.conversations.desc": "Chat with our AI assistant about your gift-giving needs and get personalized recommendations through natural conversation.",
    "card.analysis.desc": "Tell us about the recipient's interests, age, and occasion, and get customized gift ideas that match their personality perfectly.",
    "card.optimization.desc": "Find the best gift options within your budget range, from affordable surprises to luxury recommendations with maximum impact.",
    "card.wants.desc": "People add items they genuinely want to their wishlists, not things they feel obligated to request.",
    "card.duplicates.desc": "Our reservation system prevents multiple people from buying the same gift, eliminating awkward duplicates.",
    "card.priorities.desc": "See what they want most with priority rankings, helping you choose the perfect gift within your budget.",
    "card.protection.desc": "Wishlist sharing is completely private and secure. Only invited people can see specific wishlists.",
    "card.info.desc": "Get all the details you need - size, color preferences, specific models, and personal notes.",
    "card.recipients.desc": "Watch faces light up with genuine joy when you give exactly what they've been hoping for.",
    "card.instant.desc": "Get real-time alerts via email the moment prices drop on your tracked items.",
    "card.friends.desc": "Track price drops on your friends' wishlist items and surprise them with perfectly timed gifts.",
    "card.lightning.desc": "Get priority access to flash sales and limited-time offers on your tracked items before they sell out.",
    "card.learning.desc": "Our AI analyzes patterns from millions of successful gift exchanges to understand what makes gifts special.",
    "card.personal.desc": "Considers individual interests, past purchases, age, lifestyle, and relationship context for precise targeting.",
    "card.trends.desc": "Stays current with trending products and seasonal preferences to suggest relevant, timely gifts.",
    "card.surprise.desc": "Balances expected preferences with delightful surprises to create memorable gift moments.",
    "card.context.desc": "Adapts recommendations based on your relationship with the recipient and the occasion.",
    "card.updates.desc": "Continuously learns and improves recommendations based on feedback and changing preferences.",

    // Problem Section - Give What's Wanted
    "problem.traditional.title": "😔 Traditional Gifting",
    "problem.traditional.guessing": "• Guessing what they want",
    "problem.traditional.duplicates": "• Duplicate gifts",
    "problem.traditional.returns": "• Returns and exchanges",
    "problem.traditional.wasted": "• Wasted money",
    "problem.solution.title": "✨ The Solution",
    "problem.solution.wishlist": "• Wishlist-based system",
    "problem.solution.updates": "• Real-time updates",
    "problem.solution.coordination": "• Perfect coordination",
    "problem.solution.happiness": "• Guaranteed happiness",
    "problem.results.title": "😊 Better Results",
    "problem.results.right": "• Always the right gift",
    "problem.results.excitement": "• Genuine excitement",
    "problem.results.relationships": "• Stronger relationships",
    "problem.results.spent": "• Money well spent",

    // Savings Section - Price Drop Alerts
    "savings.average": "Average savings on tracked items",
    "savings.time": "Average time from alert to purchase",
    "savings.users": "Users who buy within 24 hours",

    // Coming Soon Badge
    "badge.coming.soon": "Coming Soon",

    // Home page additional translations
    "home.hero.badge.full": "Smart Gift Management Platform",
    "home.hero.badge.short": "Smart Gifting",
    "home.hero.button.instant": "Instant Wishlist • No Signup",
    "home.hero.button.public.Randomizer": "Play Randomizer • No Signup",
    "home.hero.appstore.cta": "Download on the App Store",
    "home.hero.appstore.badge.prefix": "Download on the",
    "home.hero.googleplay.cta": "Get it on Google Play",
    "home.hero.googleplay.badge.prefix": "Get it on",
    "home.trust.secure": "Secure & Private",
    "home.trust.free": "Free to Start",
    "home.trust.social": "Social Features",
    "home.features.subtitle": "Everything you need to make gift-giving effortless and memorable",
    "home.features.learnmore": "Learn more →",
    "home.cta.badge": "Join thousands of happy users",
    "home.cta.button.try": "Try Without Account",

    // Toast messages
    "toast.copied.title": "Copied!",
    "toast.copied.description": "Nickname copied to clipboard",
    "toast.copyfailed.title": "Failed to copy",
    "toast.copyfailed.description": "Could not copy nickname to clipboard",

    // Profile section
    "profile.userdetails": "User Details",
    "profile.fullname": "Full Name",
    "profile.nickname": "Nickname",
    "profile.phone": "Phone Number",
    "profile.birthday": "Birthday",
    "profile.notset": "Not set",
    "profile.identity": "Identity",
    "profile.bio": "Bio",
    "profile.nobio": "No bio added yet",
    "profile.gender": "Gender",
    "profile.social": "Social Networks",
    "profile.instagram": "Instagram",
    "profile.facebook": "Facebook",
    "profile.edit": "Edit",
    "profile.edit.name": "Edit name",
    "profile.edit.nickname": "Edit nickname",
    "profile.edit.phone": "Edit phone number",
    "profile.edit.birthday": "Edit birthday",
    "profile.edit.bio": "Edit bio",
    "profile.edit.gender": "Edit gender",
    "profile.edit.instagram": "Edit Instagram",
    "profile.edit.facebook": "Edit Facebook",
    "profile.avatar.change": "Change Your Avatar",

    // Occasions - Smart Recommendations
    "occasions.birthdays": "🎂 Birthdays",
    "occasions.birthdays.desc": "Get age-appropriate recommendations that match their current interests and hobbies.",
    "occasions.birthdays.age": "• Considers age and life stage",
    "occasions.birthdays.interests": "• Matches current interests",
    "occasions.birthdays.balance": "• Balances practical and fun gifts",
    "occasions.holidays": "🎄 Holidays",
    "occasions.holidays.desc": "Seasonal recommendations that capture the holiday spirit while staying personal.",
    "occasions.holidays.themed": "• Holiday-themed suggestions",
    "occasions.holidays.family": "• Family-friendly options",
    "occasions.holidays.trends": "• Seasonal trends included",
    "occasions.anniversaries": "💍 Anniversaries",
    "occasions.anniversaries.desc": "Romantic and meaningful suggestions that celebrate your unique relationship.",
    "occasions.anniversaries.milestone": "• Relationship milestone aware",
    "occasions.anniversaries.romantic": "• Romantic and thoughtful",
    "occasions.anniversaries.memory": "• Memory-making focused",
    "occasions.graduations": "🎓 Graduations",
    "occasions.graduations.desc": "Achievement-celebrating gifts that support their next life chapter.",
    "occasions.graduations.career": "• Career stage appropriate",
    "occasions.graduations.future": "• Future-focused suggestions",
    "occasions.graduations.achievement": "• Achievement celebration",

    // Chrome Extension
    "chrome.extension.badge": "Now Available for Chrome",
    "chrome.extension.hero.title": "Add Items to Your Wishlist from Anywhere",
    "chrome.extension.hero.subtitle": "Install our Chrome extension and add products to your BuyForS wishlist from any website with just one click. Auto-detect prices, images, and details instantly.",
    "chrome.extension.install": "Install Extension",
    "chrome.extension.createaccount": "Create Free Account",
    "chrome.extension.howitworks.title": "How It Works",
    "chrome.extension.howitworks.step1.title": "Install Extension",
    "chrome.extension.howitworks.step1.desc": "Add the BuyForS extension to Chrome in one click. Sign in with your BuyForS account or create a new one.",
    "chrome.extension.howitworks.step2.title": "Browse Any Store",
    "chrome.extension.howitworks.step2.desc": "Shop on Amazon, eBay, Etsy, or any online store. Find products you love anywhere on the web.",
    "chrome.extension.howitworks.step3.title": "Click & Save",
    "chrome.extension.howitworks.step3.desc": "Click the extension icon to instantly add the item. Product details, price, and image are auto-filled.",
    "chrome.extension.features.title": "Powerful Features",
    "chrome.extension.features.subtitle": "Everything you need to manage your wishlist across the entire web",
    "chrome.extension.feature.autodetect.title": "Auto-Detection",
    "chrome.extension.feature.autodetect.desc": "Automatically extracts product name, description, price, and images from any product page.",
    "chrome.extension.feature.universal.title": "Universal Compatibility",
    "chrome.extension.feature.universal.desc": "Works on Amazon, eBay, Etsy, Walmart, Target, and thousands of other online stores.",
    "chrome.extension.feature.pricetrack.title": "Price Tracking",
    "chrome.extension.feature.pricetrack.desc": "Enable price drop alerts for any item and get notified when prices fall.",
    "chrome.extension.feature.privacy.title": "Privacy Controls",
    "chrome.extension.feature.privacy.desc": "Choose who sees your items: public, private, or secret. Full control over your wishlist.",
    "chrome.extension.feature.sections.title": "Organized Sections",
    "chrome.extension.feature.sections.desc": "Add items directly to specific wishlist sections. Keep everything organized automatically.",
    "chrome.extension.feature.notify.title": "Friend Notifications",
    "chrome.extension.feature.notify.desc": "Notify friends when you add new items they might want to gift you.",
    "chrome.extension.security.title": "Privacy & Security First",
    "chrome.extension.security.subtitle": "Your data is encrypted and protected. We never track your browsing activity.",
    "chrome.extension.security.auth.title": "Secure Authentication",
    "chrome.extension.security.auth.desc": "Sign in securely with Google SSO or email. Your credentials are protected with industry-standard encryption.",
    "chrome.extension.security.notrack.title": "No Tracking",
    "chrome.extension.security.notrack.desc": "We only access page data when you click the extension. Your browsing history stays private.",
    "chrome.extension.security.permissions.title": "Minimal Permissions",
    "chrome.extension.security.permissions.desc": "We only request the permissions needed to function. No unnecessary access to your data.",
    "chrome.extension.security.encrypted.title": "Encrypted Storage",
    "chrome.extension.security.encrypted.desc": "All your data is encrypted and stored securely. You control who sees what.",
    "chrome.extension.cta.badge": "Available Now",
    "chrome.extension.cta.title": "Start Building Your Wishlist Today",
    "chrome.extension.cta.subtitle": "Join thousands of users who are shopping smarter with BuyForS. Install the extension and start adding items in seconds.",
    "chrome.extension.cta.install": "Install Free Extension",
    "chrome.extension.cta.account": "Create Account",
    "chrome.extension.cta.note": "Free forever · No credit card required · Works on all major sites",
    "chrome.extension.benefit.free": "Free Forever",
    "chrome.extension.benefit.works": "Works Everywhere",
    "chrome.extension.benefit.secure": "Secure & Private",
    "home.feature.chrome.title": "Chrome Extension",
    "home.feature.chrome.description": "Add items from any website with one click",
    "home.feature.dailyquiz.title": "Daily Tiny Quiz",
    "home.feature.dailyquiz.description": "Answer a few fun questions each day and see what kind of planner you are. No login required.",
    "home.feature.dailyquiz.cta": "Play now",
    "home.feature.friendquiz.title": "Friend Quiz",
    "home.feature.friendquiz.description": "Create fun quizzes about yourself and share with friends",
    "home.feature.friendquiz.cta": "Learn more",
    "home.feature.mafia.title": "Mafia Game",
    "home.feature.mafia.description": "Play the classic social deduction game with friends. Unmask the Mafia or deceive your way to victory!",
    "home.feature.sync.title": "Sync Game",
    "home.feature.sync.description": "Think like your friends, but not like everyone. Match with 2-3 people for maximum points!",
    "mafia.landing.badge": "Mafia Role Assigner",
    "mafia.landing.hero.title": "Assign Mafia Roles Instantly",
    "mafia.landing.hero.subtitle": "The easiest way to assign random roles for your Mafia party game. Add players, choose roles, and everyone gets notified secretly!",
    "mafia.landing.cta.create": "Create Game",
    "mafia.landing.cta.signup": "Get Started Free",
    "mafia.landing.cta.account": "Create Account",
    "mafia.landing.benefit.players": "6+ Players",
    "mafia.landing.benefit.roles": "8 Different Roles",
    "mafia.landing.benefit.invites": "Instant Notifications",
    "mafia.landing.howitworks.title": "How It Works",
    "mafia.landing.howitworks.step1.title": "1. Add Players",
    "mafia.landing.howitworks.step1.desc": "Add at least 6 players from your friends list or by email. They don't need an account!",
    "mafia.landing.howitworks.step2.title": "2. Start Game",
    "mafia.landing.howitworks.step2.desc": "Choose which roles to include, then click Start. Roles are randomly assigned to all players instantly.",
    "mafia.landing.howitworks.step3.title": "3. Play!",
    "mafia.landing.howitworks.step3.desc": "Everyone receives their secret role via email and notification. Now play the game in person with friends!",
    "mafia.landing.roles.title": "Available Roles",
    "mafia.landing.roles.subtitle": "Choose which roles to include in your game. Each player will be assigned one role randomly.",
    "mafia.landing.role.mafia.name": "Mafia",
    "mafia.landing.role.mafia.desc": "The bad guys. They know each other and try to eliminate the town.",
    "mafia.landing.role.godfather.name": "Godfather",
    "mafia.landing.role.godfather.desc": "Leader of the Mafia. Appears innocent when investigated.",
    "mafia.landing.role.doctor.name": "Doctor",
    "mafia.landing.role.doctor.desc": "Can protect one player from elimination each night.",
    "mafia.landing.role.detective.name": "Detective",
    "mafia.landing.role.detective.desc": "Can investigate one player each night to learn their team.",
    "mafia.landing.role.citizen.name": "Citizen",
    "mafia.landing.role.citizen.desc": "Regular townsperson with no special abilities.",
    "mafia.landing.role.vigilante.name": "Vigilante",
    "mafia.landing.role.vigilante.desc": "Town-aligned player who can eliminate someone at night.",
    "mafia.landing.role.escort.name": "Escort",
    "mafia.landing.role.escort.desc": "Can block one player's action each night.",
    "mafia.landing.role.bodyguard.name": "Bodyguard",
    "mafia.landing.role.bodyguard.desc": "Can protect a player and take the hit if attacked.",
    "mafia.landing.team.mafia": "Mafia",
    "mafia.landing.team.town": "Town",
    "mafia.landing.features.title": "Features",
    "mafia.landing.features.subtitle": "Everything you need to run a Mafia game night",
    "mafia.landing.feature.notifications.title": "Instant Notifications",
    "mafia.landing.feature.notifications.desc": "When you start the game, all players instantly receive their role via email. Registered users also get in-app notifications.",
    "mafia.landing.feature.invite.title": "Add Anyone",
    "mafia.landing.feature.invite.desc": "Add friends from your list or anyone by email. They receive their role without needing an account.",
    "mafia.landing.feature.roles.title": "Customizable Roles",
    "mafia.landing.feature.roles.desc": "Choose exactly which roles appear in your game. Create the perfect balance for your group size.",
    "mafia.landing.feature.secret.title": "Secret Assignments",
    "mafia.landing.feature.secret.desc": "Each player receives their role privately. Only they know their identity - perfect for in-person games!",
    "mafia.landing.final.badge": "Ready to Play?",
    "mafia.landing.final.title": "Create Your Game Now",
    "mafia.landing.final.subtitle": "Add your friends, pick the roles, and start assigning. It only takes a minute!",
    "mafia.landing.final.cta": "Create Game Now",
    "mafia.landing.final.note": "Free to use • No downloads required • Works on any device",
    "friendquiz.landing.badge": "Friend Quiz",
    "friendquiz.landing.hero.title": "How Well Do Your Friends Know You?",
    "friendquiz.landing.hero.subtitle": "Create fun quizzes about yourself, share with friends, and discover who really knows you best!",
    "friendquiz.landing.cta.create": "Create Your Quiz",
    "friendquiz.landing.cta.signup": "Get Started Free",
    "friendquiz.landing.cta.account": "Create Account",
    "friendquiz.landing.benefit.free": "100% Free",
    "friendquiz.landing.benefit.private": "Private to Friends",
    "friendquiz.landing.benefit.instant": "Instant Results",
    "friendquiz.landing.howitworks.title": "How It Works",
    "friendquiz.landing.howitworks.step1.title": "Create Your Quiz",
    "friendquiz.landing.howitworks.step1.desc": "Choose from template questions or create your own. Build a quiz that reflects who you are.",
    "friendquiz.landing.howitworks.step2.title": "Invite Friends",
    "friendquiz.landing.howitworks.step2.desc": "Share your quiz with friends. They'll get notified and can take the quiz anytime.",
    "friendquiz.landing.howitworks.step3.title": "See Results",
    "friendquiz.landing.howitworks.step3.desc": "View who took your quiz and how well they know you. Compare scores and have fun!",
    "friendquiz.landing.features.title": "Features",
    "friendquiz.landing.features.subtitle": "Everything you need to create engaging quizzes for your friends",
    "friendquiz.landing.feature.templates.title": "Template Questions",
    "friendquiz.landing.feature.templates.desc": "Get started quickly with pre-made question templates across various categories.",
    "friendquiz.landing.feature.friendsonly.title": "Friends Only",
    "friendquiz.landing.feature.friendsonly.desc": "Your quizzes are private. Only your friends on BuyForS can take them.",
    "friendquiz.landing.feature.notifications.title": "Instant Notifications",
    "friendquiz.landing.feature.notifications.desc": "Friends get notified when you create a quiz and you get notified when they complete it.",
    "friendquiz.landing.feature.scores.title": "Score Tracking",
    "friendquiz.landing.feature.scores.desc": "See detailed results with scores, percentages, and individual answer breakdowns.",
    "friendquiz.landing.feature.sharing.title": "Easy Sharing",
    "friendquiz.landing.feature.sharing.desc": "Share your quiz link with friends or invite them directly from the platform.",
    "friendquiz.landing.feature.privacy.title": "Private & Secure",
    "friendquiz.landing.feature.privacy.desc": "Your quiz data is private. Only you can see who responded and their answers.",
    "friendquiz.landing.privacy.title": "Your Privacy Matters",
    "friendquiz.landing.privacy.subtitle": "Friend Quiz is designed with your privacy in mind",
    "friendquiz.landing.privacy.friendsonly.title": "Friends Only Access",
    "friendquiz.landing.privacy.friendsonly.desc": "Only people you've added as friends can see and take your quizzes.",
    "friendquiz.landing.privacy.results.title": "Private Results",
    "friendquiz.landing.privacy.results.desc": "Only you can see who took your quiz and how they answered.",
    "friendquiz.landing.privacy.nopublic.title": "No Public Sharing",
    "friendquiz.landing.privacy.nopublic.desc": "Quizzes can't be shared publicly - they stay within your friend circle.",
    "friendquiz.landing.privacy.delete.title": "Delete Anytime",
    "friendquiz.landing.privacy.delete.desc": "You can delete your quiz and all responses at any time.",
    "friendquiz.landing.final.badge": "Start Today",
    "friendquiz.landing.final.title": "Ready to Find Out Who Knows You Best?",
    "friendquiz.landing.final.subtitle": "Create your first Friend Quiz and share it with your circle today.",
    "friendquiz.landing.final.cta": "Create Quiz Now",
    "friendquiz.landing.final.note": "Free forever • No credit card required",
    "sync.landing.badge": "Think Like Your Friends",
    "sync.landing.hero.title": "SYNC Game",
    "sync.landing.hero.subtitle": "A question appears with 6 options. Everyone picks one. Match with 2-3 people for maximum points. Too obvious? Zero points. Too unique? Also zero. Find your mind twins!",
    "sync.landing.cta.create": "Create Game",
    "sync.landing.cta.signup": "Get Started Free",
    "sync.landing.cta.account": "Create Account",
    "sync.landing.benefit.players": "3+ players",
    "sync.landing.benefit.rounds": "5-15 rounds",
    "sync.landing.benefit.realtime": "Real-time gameplay",
    "sync.landing.howitworks.title": "How It Works",
    "sync.landing.howitworks.step1.title": "1. Create & Invite",
    "sync.landing.howitworks.step1.desc": "Create a game room and invite 3+ friends. Choose your rounds and timing.",
    "sync.landing.howitworks.step2.title": "2. Pick Your Answer",
    "sync.landing.howitworks.step2.desc": "A question appears with 6 options. Everyone picks one secretly. Find 2-3 people who think like you!",
    "sync.landing.howitworks.step3.title": "3. Score & Win",
    "sync.landing.howitworks.step3.desc": "Answers are revealed. Match with 2-3 people = big points. Too obvious or too unique = 0 points.",
    "sync.landing.scoring.title": "Scoring System",
    "sync.landing.scoring.subtitle": "The perfect balance: match with a few, not everyone",
    "sync.landing.scoring.solo": "Too unique",
    "sync.landing.scoring.pair": "Sweet spot",
    "sync.landing.scoring.trio": "Good balance",
    "sync.landing.scoring.quad": "Getting obvious",
    "sync.landing.scoring.hivemind": "Hivemind",
    "sync.landing.features.title": "Features",
    "sync.landing.features.subtitle": "Everything you need for the perfect party game",
    "sync.landing.feature.quick.title": "Quick Rounds",
    "sync.landing.feature.quick.desc": "15-second rounds keep the game fast-paced and exciting",
    "sync.landing.feature.ai.title": "AI Questions",
    "sync.landing.feature.ai.desc": "AI-generated questions with 6 fun options. Never play the same game twice",
    "sync.landing.feature.mind.title": "Mind Twins",
    "sync.landing.feature.mind.desc": "Discover who thinks like you with special awards and match tracking",
    "sync.landing.feature.invite.title": "Easy Invites",
    "sync.landing.feature.invite.desc": "Share a link and friends can join instantly. No complicated setup",
    "sync.landing.final.badge": "Perfect for Weekend Nights",
    "sync.landing.final.title": "Ready to Find Your Mind Twins?",
    "sync.landing.final.subtitle": "Create a game, invite friends, and discover who thinks like you. Pick from 6 options, instant reveals, and endless fun.",
    "sync.landing.final.cta": "Create Game",
    "sync.landing.final.note": "Free to use • No downloads required • Works on any device"
  },
  de: {
    "home.hero.title": "Magische Geschenkmomente kreieren",
    "home.hero.subtitle": "Teile deine Wünsche, entdecke perfekte Geschenke und mache jede Gelegenheit besonders",
    "button.signup": "Registrieren",
    "button.signin": "Anmelden",
    "language.switch.to": "Wechseln zu Englisch",
    "home.cta.title": "Bereit, das Schenken magisch zu machen?",
    "home.cta.subtitle": "Tritt bei und erstelle noch heute deine Wunschlisten",
    "home.feature.wishlist.title": "Intelligente Wunschlisten",
    "home.feature.wishlist.description": "Erstelle unbegrenzt Wunschlisten, ordne Geschenke in Bereiche und teile sie mit Freunden und Familie – so weiß jeder genau, worüber du dich freust.",
    "home.feature.ai.title": "KI-Geschenkassistent",
    "home.feature.ai.description": "Beschreibe die Person und den Anlass, und unser KI-Geschenkassistent schlägt in Sekunden durchdachte, persönliche Geschenkideen vor.",
    "home.feature.social.title": "Soziales Schenken",
    "home.feature.social.description": "Verbinde dich mit Freunden, folge ihren Wunschlisten und erhalte Erinnerungen, damit du keinen Geburtstag oder besonderen Anlass verpasst.",
    "home.feature.wanted.title": "Schenke was gewünscht wird",
    "home.feature.wanted.description": "Freunde reservieren die Geschenke, die sie kaufen – so gibt es keine Dopplungen und jedes Geschenk ist wirklich gewünscht.",
    "home.feature.price.title": "Preisalarme",
    "home.feature.price.description": "Verfolge Preise automatisch und werde benachrichtigt, sobald Artikel auf deiner oder der Wunschliste deiner Freunde günstiger werden.",
    "home.feature.recommendation.title": "Intelligente Empfehlungen",
    "home.feature.recommendation.description": "Entdecke Geschenke, die auf den einzigartigen Geschmack jeder Person zugeschnitten sind",
    "home.feature.Randomizer.title": "Wichteln",
    "home.feature.Randomizer.description": "Erstelle Events, verwalte Teilnehmer und lass unser System zufällig zuordnen, wer wem schenkt!",
    "home.feature.eventmanagement.title": "Eventmanagement",
    "home.feature.eventmanagement.description": "Plane Events von A bis Z – lade Gäste ein, verfolge Zusagen, verteile Aufgaben und Sitzplätze und behalte jedes Detail im Blick.",
    "home.section.why": "Warum Menschen unsere Plattform lieben",
    "home.create.wishlist.nologin": "Wunschliste ohne Anmeldung erstellen",
    "nav.about": "Über uns",
    "nav.contact": "Kontakt",
    "nav.faq": "FAQ",
    "nav.privacy": "Datenschutz",
    "nav.terms": "Nutzungsbedingungen",
    "nav.impressum": "Impressum",
    "login.welcome": "Willkommen zurück",
    "login.continue.email": "Oder weiter mit E-Mail",
    "login.email": "E-Mail",
    "login.password": "Passwort",
    "login.forgot": "Passwort vergessen?",
    "login.no.account": "Noch kein Konto?",
    "login.register": "Registrieren",
    "register.create": "Konto erstellen",
    "register.or.email": "Oder registriere dich mit E-Mail",
    "register.have.account": "Hast du bereits ein Konto?",
    "forgot.title": "Passwort vergessen?",
    "forgot.description": "Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen des Passworts",
    "forgot.email.placeholder": "Gib deine E-Mail ein",
    "forgot.sending": "Wird gesendet...",
    "forgot.send": "Link zum Zurücksetzen senden",
    "forgot.back": "Zurück zur Anmeldung",
    "footer.copyright": "© {year} BuyForS. Alle Rechte vorbehalten.",
    "wishlist.create.title": "Erstelle deine Wunschliste",
    "wishlist.create.subtitle": "Füge Artikel zu deiner Wunschliste hinzu und teile sie mit Freunden und Familie.",
    "wishlist.create.add": "Wunschlisten-Artikel hinzufügen",
    "wishlist.create.email": "Deine E-Mail",
    "wishlist.create.email.placeholder": "deine.email@beispiel.com",
    "wishlist.create.important": "Wichtig",
    "wishlist.create.email.note": "Eine E-Mail-Adresse ist erforderlich, um deine Wunschliste später bearbeiten zu können. Ohne E-Mail erhältst du nur einen Link zum Teilen und kannst keine Artikel hinzufügen oder bearbeiten.",
    "wishlist.create.create": "Wunschliste erstellen",
    "wishlist.create.empty": "Deine Wunschliste ist leer. Füge einige Artikel hinzu!",
    "wishlist.create.account": "Erstelle ein Konto",
    "wishlist.create.account.desc": "Registriere dich, um deine Wunschliste zu speichern und weitere Funktionen zu nutzen!",
    "wishlist.create.signup": "Jetzt registrieren",
    "wishlist.create.ready": "Deine Wunschliste ist fertig!",
    "wishlist.create.shareurl": "Teil-URL:",
    "wishlist.create.editurl": "Bearbeitungs-URL:",
    "wishlist.create.copy.share": "Teil-Link kopieren",
    "wishlist.create.copy.edit": "Bearbeitungs-Link kopieren",
    "wishlist.create.no.edit": "Es ist kein Bearbeitungs-Link verfügbar, da du keine E-Mail angegeben hast. Du wirst diese Wunschliste später nicht bearbeiten können.",
    "wishlist.create.sent": "Links wurden gesendet an",
    "wishlist.create.home": "Startseite",
    "wishlist.item.view": "Produkt ansehen",
    "wishlist.item.view.link": "Produktlink ansehen",
    "wishlist.create.email.invalid": "Bitte gib eine gültige E-Mail-Adresse ein",
    "wishlist.create.creating": "Wird erstellt...",
    "wishlist.create.items": "Artikel",
    "wishlist.create.email.sent.title": "E-Mail gesendet",
    "wishlist.create.email.sent": "Ein Link zu deiner Wunschliste wurde an {email} gesendet",
    "wishlist.create.error.title": "Fehler beim Erstellen der Wunschliste",
    "wishlist.create.error.description": "Bitte versuche es erneut.",

    "public.Randomizer.title": "Wichteln spielen",
    "public.Randomizer.subtitle": "Erstelle und ziehe Namen sofort - keine Registrierung erforderlich!",
    "public.Randomizer.home": "Startseite",
    "public.Randomizer.game.name": "Spielname",
    "public.Randomizer.game.name.placeholder": "z.B. Familien-Wichteln 2023",
    "public.Randomizer.participants": "Teilnehmer",
    "public.Randomizer.add.participant": "Teilnehmer hinzufügen",
    "public.Randomizer.email": "E-Mail",
    "public.Randomizer.name": "Name",
    "public.Randomizer.name.placeholder": "Vollständiger Name",
    "public.Randomizer.min.participants": "Mindestens 3 Teilnehmer erforderlich",
    "public.Randomizer.budget.max": "Maximales Budget",
    "public.Randomizer.optional": "Optional",
    "public.Randomizer.exclusions": "Ausschlüsse",
    "public.Randomizer.add.exclusion": "Ausschluss hinzufügen",
    "public.Randomizer.exclusion.participant": "Teilnehmer",
    "public.Randomizer.cannot.match": "kann nicht zugeordnet werden",
    "public.Randomizer.exclusion.excluded": "Ausgeschlossener Teilnehmer",
    "public.Randomizer.exclusions.description": "Regeln festlegen, um zu verhindern, dass bestimmte Teilnehmer einander zugeordnet werden. Ausschlüsse sind bidirektional - wenn du 'A kann nicht B zugeordnet werden' festlegst, wird A nicht B zugeordnet UND B wird nicht A zugeordnet.",
    "public.Randomizer.draw": "Namen ziehen",
    "public.Randomizer.drawing": "Ziehen...",
    "public.Randomizer.success.title": "Wichteln-Ziehung abgeschlossen!",
    "public.Randomizer.success.description": "E-Mails wurden an {count} Teilnehmer mit ihren Zuweisungen gesendet.",
    "public.Randomizer.success.check.email": "Jeder Teilnehmer erhält eine E-Mail mit dem Namen der Person, für die er ein Geschenk kaufen soll.",
    "public.Randomizer.create.another": "Weiteres erstellen",
    "public.Randomizer.go.home": "Zur Startseite",
    "public.Randomizer.error.game.name": "Bitte gib einen Spielnamen ein",
    "public.Randomizer.error.min.participants": "Mindestens 3 Teilnehmer sind erforderlich",
    "public.Randomizer.error.invalid.email": "Ungültige E-Mail: {email}",
    "public.Randomizer.error.duplicate.emails": "Doppelte E-Mail-Adressen sind nicht erlaubt",
    "public.Randomizer.error.exclusion.not.found": "Teilnehmer nicht gefunden: {email}",
    "public.Randomizer.error.exclusion.self": "Ein Teilnehmer kann sich nicht selbst ausschließen",
    "public.Randomizer.error.draw": "Fehler beim Erstellen des Wichteln-Spiels",
    "public.Randomizer.error.draw.description": "Bitte überprüfe deine Eingaben und versuche es erneut.",

    "wishlist.edit.title": "Bearbeite deine Wunschliste",
    "wishlist.edit.subtitle": "Aktualisiere oder entferne Artikel aus deiner Wunschliste.",
    "wishlist.edit.empty": "Deine Wunschliste ist leer.",
    "wishlist.edit.delete": "Wunschliste löschen",
    "wishlist.edit.delete.confirm": "Bist du absolut sicher?",
    "wishlist.edit.delete.desc": "Dies wird deine Wunschliste und alle ihre Artikel dauerhaft löschen. Diese Aktion kann nicht rückgängig gemacht werden.",
    "wishlist.edit.delete.cancel": "Abbrechen",
    "wishlist.edit.delete.confirm.btn": "Wunschliste löschen",
    "wishlist.edit.deleting": "Wird gelöscht...",
    "wishlist.edit.verify.title": "Verifiziere deine E-Mail",
    "wishlist.edit.verify.subtitle": "Um deine Wunschliste zu bearbeiten, gib bitte die E-Mail-Adresse ein, die du bei der Erstellung verwendet hast.",
    "wishlist.edit.verify.email": "E-Mail",
    "wishlist.edit.verify.send": "Verifizierungstoken senden",
    "wishlist.edit.verify.sending": "Wird gesendet...",
    "wishlist.edit.verify.token": "Verifizierungstoken",
    "wishlist.edit.verify.token.placeholder": "Füge deinen Verifizierungstoken hier ein",
    "wishlist.edit.verify.token.btn": "Token verifizieren",
    "wishlist.edit.verify.verifying": "Wird verifiziert...",

    "wishlist.share.title": "Wunschliste",
    "wishlist.share.subtitle": "Hier sind die Artikel, die jemand gerne erhalten würde!",
    "wishlist.share.empty": "Diese Wunschliste ist leer.",
    "wishlist.share.create": "Erstelle deine eigene Wunschliste mit",

    // SimpleWishlistDialog translations
    "wishlist.item.add": "Wunschlisten-Artikel hinzufügen",
    "wishlist.item.edit": "Wunschlisten-Artikel bearbeiten",
    "wishlist.item.name": "Artikelname",
    "wishlist.item.name.placeholder": "Artikelname eingeben",
    "wishlist.item.description": "Beschreibung",
    "wishlist.item.description.placeholder": "Optionale Beschreibung",
    "wishlist.item.link": "Produktlink",
    "wishlist.item.link.placeholder": "https://beispiel.de/produkt",
    "wishlist.item.saving": "Wird gespeichert...",
    "wishlist.item.update": "Artikel aktualisieren",
    "wishlist.item.add.button": "Zur Wunschliste hinzufügen",
    "wishlist.powered.by": "Unterstützt von",
    "wishlist.create.copy.success": "Kopiert!",
    "wishlist.create.copy.share.success": "Share-Link in Zwischenablage kopiert",
    "wishlist.create.copy.edit.success": "Bearbeitungs-Link in Zwischenablage kopiert",
    "wishlist.create.copy.error": "Kopieren fehlgeschlagen",
    "wishlist.create.copy.error.desc": "Link konnte nicht in Zwischenablage kopiert werden",

    // Added error messages
    "wishlist.item.removed": "Artikel entfernt",
    "wishlist.item.removed.desc": "Der Wunschlistenartikel wurde entfernt",
    "wishlist.error.load": "Fehler beim Laden der Wunschliste",
    "wishlist.error.load.invalid": "Diese Wunschliste konnte nicht geladen werden. Sie wurde möglicherweise entfernt oder der Link ist ungültig.",
    "wishlist.error.auth.expired": "Authentifizierung abgelaufen",
    "wishlist.error.auth.expired.desc": "Bitte verifiziere deine E-Mail erneut, um fortzufahren",
    "wishlist.error.update": "Fehler",
    "wishlist.error.update.desc": "Aktualisierung des Wunschlistenartikels fehlgeschlagen",
    "wishlist.error.update.failed": "Aktualisierung des Wunschlistenartikels fehlgeschlagen",
    "wishlist.error.delete": "Fehler",
    "wishlist.error.delete.desc": "Löschen des Wunschlistenartikels fehlgeschlagen",
    "wishlist.error.delete.wishlist": "Fehler",
    "wishlist.error.delete.wishlist.desc": "Fehler beim Löschen der Wunschliste. Bitte versuche es später erneut.",
    "wishlist.error.missing.uuid": "UUID fehlt",
    "wishlist.error.missing.token": "Authentifizierungstoken fehlt",
    "wishlist.error.verification.failed": "E-Mail-Verifizierung fehlgeschlagen",
    "wishlist.error.invalid.token": "Ungültiger Verifizierungstoken",

    // Landing pages translations
    "landing.nav.login": "Anmelden",
    "landing.nav.signup": "Kostenlos registrieren",
    "landing.footer.tagline": "Die beste Plattform für die Organisation von Wichteln und das Verwalten von Wunschlisten.",
    "landing.footer.features": "Funktionen",
    "landing.footer.support": "Support",
    "landing.footer.legal": "Rechtliches",
    "landing.footer.copyright": "© {year} BuyForS. Alle Rechte vorbehalten.",

    // Footer links
    "footer.link.Randomizer": "Wichteln",
    "footer.link.smartWishlists": "Intelligente Wunschlisten",
    "footer.link.aiGiftAssistant": "KI-Geschenkassistent",
    "footer.link.socialGifting": "Soziales Schenken",
    "footer.link.giveWhatsWanted": "Schenke was gewünscht wird",
    "footer.link.priceDropAlerts": "Preisalarme",
    "footer.link.smartRecommendations": "Intelligente Empfehlungen",
    "footer.link.eventManagement": "Eventmanagement",
    "footer.link.faq": "FAQ",
    "footer.link.contact": "Kontakt",
    "footer.link.about": "Über uns",
    "footer.link.privacyPolicy": "Datenschutzrichtlinie",
    "footer.link.termsOfService": "Nutzungsbedingungen",
    "footer.link.impressum": "Impressum",
    "footer.link.chromeExtension": "Chrome-Erweiterung",
    "footer.link.status": "Status",

    // Landing page taglines
    "wishlists.footer.tagline": "Die intelligenteste Plattform zum Erstellen und Teilen von Wunschlisten mit Freunden und Familie.",
    "ai.footer.tagline": "KI-gestützte Geschenkempfehlungen, die jeden Anlass besonders machen.",
    "social.footer.tagline": "Die soziale Plattform, die Schenken zu einer freudigen, verbundenen Erfahrung macht.",
    "wanted.footer.tagline": "Schenken wird zur Freude für Schenkende und Beschenkte durch wunschlistenbasierte Geschenke.",
    "price.footer.tagline": "Intelligente Preisüberwachung, die Ihnen bei jedem Kauf Geld spart.",
    "recommendations.footer.tagline": "KI-gestützte Geschenkempfehlungen, die jeden Anlass besonders machen.",

    // Randomizer Landing
    "Randomizer.landing.title": "Kostenloser Wichtel-Generator",
    "Randomizer.landing.subtitle": "Erstelle und verwalte Wichtel-Geschenkaustausche mühelos. Setze Budgets, schließe Teilnehmer aus, sende Einladungen und mache deine Feiertagsfeierungen unvergesslich mit unserem kostenlosen Online-Tool.",
    "Randomizer.landing.cta.primary": "Kostenloses Wichteln erstellen",
    "Randomizer.landing.cta.secondary": "Anmelden",
    "Randomizer.landing.features.title": "Warum unseren Wichtel-Generator wählen?",
    "Randomizer.landing.how.title": "Wie es funktioniert",
    "Randomizer.landing.faq.title": "Häufig gestellte Fragen",
    "Randomizer.landing.final.title": "Bereit, dein Wichtel-Event zu erstellen?",
    "Randomizer.landing.final.subtitle": "Schließe dich Tausenden von Nutzern an, die ihre Feiertagsgeschenkaustausche mit BuyForS magisch gemacht haben",
    "Randomizer.landing.final.cta": "Kostenlos loslegen",
    "Randomizer.landing.games.title": "Beliebte Geschenkaustausch-Spiele",
    "Randomizer.landing.games.subtitle": "Unser Randomizer funktioniert perfekt für all diese beliebten Geschenkaustausch-Spiele und mehr!",
    "Randomizer.landing.games.secretsanta.title": "Wichteln",
    "Randomizer.landing.games.secretsanta.desc": "Der klassische Feiertags-Geschenkaustausch, bei dem jeder einen geheimen Geber erhält",
    "Randomizer.landing.games.whiteelephant.title": "White Elephant",
    "Randomizer.landing.games.whiteelephant.desc": "Lustiges Geschenk-Tauschspiel, bei dem Teilnehmer Geschenke stehlen und tauschen können",
    "Randomizer.landing.games.yankeeswap.title": "Yankee Swap",
    "Randomizer.landing.games.yankeeswap.desc": "Geschenkaustausch mit mehreren Runden des Tauschens und Handels",
    "Randomizer.landing.games.dirtysanta.title": "Dirty Santa",
    "Randomizer.landing.games.dirtysanta.desc": "Wettbewerbsorientierter Geschenkaustausch mit Stehlen und strategischem Gameplay",
    "Randomizer.landing.games.kriskringle.title": "Kris Kringle",
    "Randomizer.landing.games.kriskringle.desc": "Traditionelles Namensziehspiel, das in vielen Kulturen beliebt ist",
    "Randomizer.landing.games.pollyanna.title": "Pollyanna",
    "Randomizer.landing.games.pollyanna.desc": "Freundlicher Geschenkaustausch, bei dem jeder heimlich einen Namen zieht",
    "Randomizer.landing.games.giftswap.title": "Geschenk-Tausch",
    "Randomizer.landing.games.giftswap.desc": "Einfacher und unterhaltsamer Geschenkaustausch, perfekt für jeden Anlass",
    "Randomizer.landing.games.mysterygift.title": "Mystery Geschenkaustausch",
    "Randomizer.landing.games.mysterygift.desc": "Sorge für Aufregung mit anonymem Geben und Empfangen von Geschenken",

    // Smart Wishlists Landing
    "wishlists.landing.title": "Intelligente Wunschlisten",
    "wishlists.landing.subtitle": "Erstelle intelligente Wunschlisten, die sich an deine Vorlieben anpassen. Teile mit Freunden und Familie, erhalte Preisalarme und bekomme nie wieder ungewollte Geschenke.",
    "wishlists.landing.cta.primary": "Deine intelligente Wunschliste erstellen",
    "wishlists.landing.cta.secondary": "Ohne Konto ausprobieren",
    "wishlists.landing.features.title": "Warum intelligente Wunschlisten?",
    "wishlists.landing.how.title": "Wie es funktioniert",
    "wishlists.landing.final.title": "Bereit, deine intelligente Wunschliste zu erstellen?",
    "wishlists.landing.final.subtitle": "Schließe dich Tausenden an, die ihr Schenken und Beschenktwerden revolutioniert haben",

    // AI Gift Assistant Landing
    "ai.landing.title": "KI-Geschenkassistent",
    "ai.landing.subtitle": "Erhalte KI-gestützte Geschenkempfehlungen, die auf den einzigartigen Geschmack jeder Person zugeschnitten sind. Unser intelligenter Assistent analysiert Vorlieben, um jedes Mal perfekte Geschenke vorzuschlagen.",
    "ai.landing.cta.primary": "KI-Geschenkassistent ausprobieren",
    "ai.landing.cta.secondary": "Mehr erfahren",
    "ai.landing.features.title": "Angetrieben von fortschrittlicher KI",
    "ai.landing.how.title": "Wie der KI-Geschenkassistent funktioniert",
    "ai.landing.faq.title": "Häufige Fragen",
    "ai.landing.final.title": "Bereit, das perfekte Geschenk zu finden?",
    "ai.landing.final.subtitle": "Lass die KI dir helfen, der beste Geschenkgeber in deinem Umfeld zu werden",

    // Social Gifting Landing
    "social.landing.title": "Soziale Geschenk-Plattform",
    "social.landing.subtitle": "Verbinde dich mit Freunden und Familie, um keine wichtigen Anlässe mehr zu verpassen. Teile Wunschlisten, koordiniere Gruppengeschenke und mache jede Feier besonders.",
    "social.landing.cta.primary": "Der Gemeinschaft beitreten",
    "social.landing.cta.secondary": "Soziale Funktionen erkunden",
    "social.landing.features.title": "Verbinden und gemeinsam feiern",
    "social.landing.how.title": "Wie soziales Schenken funktioniert",
    "social.landing.benefits.title": "Warum soziales Schenken wählen?",
    "social.landing.final.title": "Bereit, dein Schenken zu transformieren?",
    "social.landing.final.subtitle": "Tritt unserer sozialen Geschenk-Gemeinschaft bei und mache jede Feier unvergesslich",

    // Event Management Landing
    "events.landing.title": "Perfekte Events planen & veranstalten",
    "events.landing.subtitle": "Von kleinen Zusammenkünften bis zu großen Feiern – verwalten Sie jedes Detail mit unserer umfassenden Event-Plattform. RSVP verfolgen, Aufgaben koordinieren und unvergessliche Erlebnisse schaffen.",
    "events.landing.cta": "Event-Planung starten",
    "events.landing.features.title": "Alles, was Sie für großartige Events brauchen",
    "events.landing.feature.guests.title": "Gästeverwaltung",
    "events.landing.feature.guests.desc": "Einladungen versenden, RSVPs verfolgen, Begleitpersonen verwalten und Ihre Gästeliste an einem Ort organisiert halten.",
    "events.landing.feature.timeline.title": "Event-Zeitplan",
    "events.landing.feature.timeline.desc": "Detaillierte Zeitpläne erstellen, damit jeder weiß, was während Ihrer Veranstaltung wann passiert.",
    "events.landing.feature.tasks.title": "Aufgabenkoordination",
    "events.landing.feature.tasks.desc": "Aufgaben an Teilnehmer zuweisen, Fortschritte verfolgen und sicherstellen, dass nichts übersehen wird.",
    "events.landing.feature.seating.title": "Sitzpläne",
    "events.landing.feature.seating.desc": "Perfekte Sitzordnungen mit unserem visuellen Sitzplan-Tool planen - ideal für Dinner und Empfänge.",
    "events.landing.feature.qr.title": "QR-Check-in",
    "events.landing.feature.qr.desc": "Event-Check-in mit QR-Codes optimieren. Anwesenheit verfolgen und Gäste effizient begrüßen.",
    "events.landing.feature.thanks.title": "Danksagungen",
    "events.landing.feature.thanks.desc": "Personalisierte Dankesnachrichten mit einem Klick nach Ihrer Veranstaltung an Gäste senden.",
    "events.landing.how.title": "So funktioniert es",
    "events.landing.how.step1.title": "Event erstellen",
    "events.landing.how.step1.desc": "Event-Details einrichten einschließlich Datum, Zeit, Ort und Art. Sichtbarkeitseinstellungen und Teilnehmerlimits wählen.",
    "events.landing.how.step2.title": "Gäste einladen",
    "events.landing.how.step2.desc": "Teilnehmer aus Ihrer Freundesliste hinzufügen oder per E-Mail einladen. Schöne Einladungen versenden und RSVPs in Echtzeit verfolgen.",
    "events.landing.how.step3.title": "Details koordinieren",
    "events.landing.how.step3.desc": "Aufgaben verwalten, Zeitpläne erstellen, Sitzordnung planen und Event-Medien hochladen - alles von einem Dashboard.",
    "events.landing.how.step4.title": "Veranstalten & Genießen",
    "events.landing.how.step4.desc": "QR-Check-in für nahtlose Ankunft nutzen und nach dem Event Danksagungen versenden, um Ihre Wertschätzung zu zeigen.",
    "events.landing.occasions.title": "Perfekt für jeden Anlass",
    "events.landing.occasions.birthdays": "Geburtstagsfeiern",
    "events.landing.occasions.weddings": "Hochzeiten",
    "events.landing.occasions.corporate": "Firmenevents",
    "events.landing.occasions.gatherings": "Zusammenkünfte",
    "events.landing.final.title": "Bereit, Ihr perfektes Event zu planen?",
    "events.landing.final.subtitle": "Treten Sie Tausenden von Gastgebern bei, die BuyForS vertrauen, um ihre Events unvergesslich und stressfrei zu machen.",
    "events.landing.final.cta": "Kostenlos starten",

    // Give What's Wanted Landing
    "wanted.landing.title": "Schenke was gewünscht wird",
    "wanted.landing.subtitle": "Stelle sicher, dass deine Geschenke immer geschätzt werden mit unserem wunschlistenbasierten Schenksystem. Schenke genau das, was gewünscht wird, nicht was du denkst, dass sie brauchen.",
    "wanted.landing.cta.primary": "Besser schenken beginnen",
    "wanted.landing.cta.secondary": "Wunschliste erstellen",
    "wanted.landing.problem.title": "Beende das Rätselraten beim Schenken für immer",
    "wanted.landing.problem.subtitle": "Keine Rückgaben unerwünschter Geschenke mehr, keine verlegenen Lächeln, kein verschwendetes Geld mehr. Schenke Geschenke, die jedes Mal wirklich erfreuen.",
    "wanted.landing.features.title": "Wie wir perfekte Geschenke sicherstellen",
    "wanted.landing.success.title": "Echte Erfolgsgeschichten",
    "wanted.landing.final.title": "Bereit, perfekte Geschenke zu machen?",
    "wanted.landing.final.subtitle": "Schließe dich Tausenden an, die ihr Schenken mit wunschlistenbasierten Geschenken transformiert haben",

    // Price Drop Alerts Landing
    "price.landing.title": "Preisalarme",
    "price.landing.subtitle": "Zahle nie wieder zu viel! Erhalte sofortige Benachrichtigungen, wenn die Preise für deine Wunschlistenartikel und die deiner Freunde fallen. Spare Geld bei jedem Kauf.",
    "price.landing.cta.primary": "Geld sparen beginnen",
    "price.landing.cta.secondary": "Artikel zum Verfolgen hinzufügen",
    "price.landing.savings.title": "💰 Nutzer sparen durchschnittlich 247€ pro Jahr",
    "price.landing.features.title": "Intelligente Preisüberwachung",
    "price.landing.how.title": "Wie Preisverfolgung funktioniert",
    "price.landing.final.title": "Bereit, Geld zu sparen?",
    "price.landing.final.subtitle": "Schließe dich Tausenden an, die dank unserer intelligenten Preisalarme nie zu viel zahlen",

    // Smart Recommendations Landing
    "recommendations.landing.title": "Intelligente Empfehlungen",
    "recommendations.landing.subtitle": "Entdecke Geschenke, die auf den einzigartigen Geschmack jeder Person zugeschnitten sind, mit unserer fortschrittlichen Empfehlungsengine. Finde jedes Mal das perfekte Geschenk mit KI-gestützten Einblicken.",
    "recommendations.landing.cta.primary": "Personalisierte Empfehlungen erhalten",
    "recommendations.landing.cta.secondary": "Empfehlungen erkunden",
    "recommendations.landing.features.title": "Angetrieben von fortschrittlicher KI",
    "recommendations.landing.how.title": "Wie intelligente Empfehlungen funktionieren",
    "recommendations.landing.occasions.title": "Perfekt für jeden Anlass",
    "recommendations.landing.stats.title": "Bewährte Ergebnisse",
    "recommendations.landing.final.title": "Bereit, perfekte Geschenke zu entdecken?",
    "recommendations.landing.final.subtitle": "Lass unsere KI dir helfen, der Geschenkgeber zu werden, an den sich jeder erinnert",

    // Card Titles and Content - Landing Pages
    "card.smart.exclusions": "Intelligente Ausschlüsse",
    "card.automatic.invitations": "Automatische Einladungen",
    "card.free": "100% Kostenlos",
    "card.friend.network": "Freundesnetzwerk",
    "card.gift.reservations": "Geschenkreservierungen",
    "card.smart.notifications": "Intelligente Benachrichtigungen",
    "card.intelligent.organization": "Intelligente Organisation",
    "card.price.tracking": "Preisverfolgung",
    "card.detailed.preferences": "Detaillierte Präferenzen",
    "card.smart.conversations": "Intelligente Gespräche",
    "card.personalized.analysis": "Personalisierte Analyse",
    "card.budget.optimization": "Budget-Optimierung",
    "card.genuine.wants": "Echte Wünsche",
    "card.no.duplicates": "Keine Duplikate",
    "card.priority.levels": "Prioritätsstufen",
    "card.privacy.protection": "Datenschutz",
    "card.detailed.info": "Detaillierte Informationen",
    "card.happy.recipients": "Glückliche Beschenkte",
    "card.instant.notifications": "Sofortige Benachrichtigungen",
    "card.friends.wishlists": "Freunde-Wunschlisten",
    "card.lightning.deals": "Blitzangebote",
    "card.deep.learning": "Deep Learning",
    "card.personal.preferences": "Persönliche Präferenzen",
    "card.trend.analysis": "Trendanalyse",
    "card.surprise.factor": "Überraschungsfaktor",
    "card.social.context": "Sozialer Kontext",
    "card.realtime.updates": "Echtzeit-Updates",

    // Card Content Text
    "card.exclusions.desc": "Verhindere, dass Paare, Familienmitglieder oder bestimmte Teilnehmer sich gegenseitig zugewiesen werden mit erweiterten Ausschlussregeln.",
    "card.invitations.desc": "Sende automatisch E-Mail-Einladungen mit Wichtel-Zuweisungen. Teilnehmer erhalten die Informationen ihres Beschenkten sicher.",
    "card.free.desc": "Unser Wichtel-Generator ist völlig kostenlos nutzbar. Erstelle unbegrenzte Events und lade bis zu 100 Teilnehmer ein.",
    "card.network.desc": "Verbinde dich mit Freunden und Familienmitgliedern, um ihre Wunschlisten und wichtigen Termine zu sehen. Baue dein Geschenknetzwerk auf und verpasse nie besondere Anlässe.",
    "card.reservations.desc": "Reserviere heimlich Artikel aus Freundes-Wunschlisten, um doppelte Geschenke zu vermeiden. Koordiniere mit anderen ohne die Überraschung zu verderben.",
    "card.notifications.desc": "Erhalte intelligente Benachrichtigungen über bevorstehende Anlässe, neue Wunschlistenartikel und Geschenkmöglichkeiten aus deinem sozialen Netzwerk.",
    "card.organization.desc": "Kategorisiere und organisiere deine Wunschlistenartikel automatisch mit intelligenten Bereichen und Tags für einfaches Durchsuchen.",
    "card.tracking.desc": "Verfolge automatisch Preisänderungen bei deinen Wunschlistenartikeln und erhalte Benachrichtigungen wenn sie im Angebot sind.",
    "card.preferences.desc": "Füge persönliche Notizen, bevorzugte Farben, Größen und alternative Optionen hinzu, um anderen zu helfen, das perfekte Geschenk zu wählen.",
    "card.conversations.desc": "Chatte mit unserem KI-Assistenten über deine Geschenkbedürfnisse und erhalte personalisierte Empfehlungen durch natürliche Gespräche.",
    "card.analysis.desc": "Erzähle uns über die Interessen, das Alter und den Anlass des Beschenkten und erhalte maßgeschneiderte Geschenkideen, die perfekt zu ihrer Persönlichkeit passen.",
    "card.optimization.desc": "Finde die besten Geschenkoptionen in deinem Budgetbereich, von erschwinglichen Überraschungen bis zu Luxusempfehlungen mit maximaler Wirkung.",
    "card.wants.desc": "Menschen fügen Artikel zu ihren Wunschlisten hinzu, die sie wirklich wollen, nicht Dinge, die sie sich verpflichtet fühlen anzufordern.",
    "card.duplicates.desc": "Unser Reservierungssystem verhindert, dass mehrere Personen das gleiche Geschenk kaufen und eliminiert peinliche Duplikate.",
    "card.priorities.desc": "Sieh was sie am meisten wollen mit Prioritätsrankings, die dir helfen, das perfekte Geschenk in deinem Budget zu wählen.",
    "card.protection.desc": "Das Teilen von Wunschlisten ist völlig privat und sicher. Nur eingeladene Personen können bestimmte Wunschlisten sehen.",
    "card.info.desc": "Erhalte alle Details die du brauchst - Größe, Farbpräferenzen, spezifische Modelle und persönliche Notizen.",
    "card.recipients.desc": "Sieh wie Gesichter vor echter Freude leuchten, wenn du genau das gibst, worauf sie gehofft haben.",
    "card.instant.desc": "Erhalte Echtzeit-Alarme per E-Mail sobald die Preise bei deinen verfolgten Artikeln fallen.",
    "card.friends.desc": "Verfolge Preissenkungen bei den Wunschlistenartikeln deiner Freunde und überrasche sie mit perfekt getimten Geschenken.",
    "card.lightning.desc": "Erhalte prioritären Zugang zu Blitzverkäufen und zeitlich begrenzten Angeboten bei deinen verfolgten Artikeln bevor sie ausverkauft sind.",
    "card.learning.desc": "Unsere KI analysiert Muster von Millionen erfolgreicher Geschenkaustausche, um zu verstehen, was Geschenke besonders macht.",
    "card.personal.desc": "Berücksichtigt individuelle Interessen, frühere Käufe, Alter, Lebensstil und Beziehungskontext für präzise Zielgruppenansprache.",
    "card.trends.desc": "Bleibt aktuell mit Trendprodukten und saisonalen Präferenzen, um relevante, zeitgemäße Geschenke vorzuschlagen.",
    "card.surprise.desc": "Balanciert erwartete Präferenzen mit entzückenden Überraschungen, um unvergessliche Geschenkmomente zu schaffen.",
    "card.context.desc": "Passt Empfehlungen basierend auf deiner Beziehung zum Beschenkten und dem Anlass an.",
    "card.updates.desc": "Lernt kontinuierlich und verbessert Empfehlungen basierend auf Feedback und sich ändernden Präferenzen.",

    // Problem Section - Give What's Wanted
    "problem.traditional.title": "😔 Traditionelles Schenken",
    "problem.traditional.guessing": "• Raten was sie wollen",
    "problem.traditional.duplicates": "• Doppelte Geschenke",
    "problem.traditional.returns": "• Rückgaben und Umtausch",
    "problem.traditional.wasted": "• Verschwendetes Geld",
    "problem.solution.title": "✨ Die Lösung",
    "problem.solution.wishlist": "• Wunschlisten-basiertes System",
    "problem.solution.updates": "• Echtzeit-Updates",
    "problem.solution.coordination": "• Perfekte Koordination",
    "problem.solution.happiness": "• Garantiertes Glück",
    "problem.results.title": "😊 Bessere Ergebnisse",
    "problem.results.right": "• Immer das richtige Geschenk",
    "problem.results.excitement": "• Echte Begeisterung",
    "problem.results.relationships": "• Stärkere Beziehungen",
    "problem.results.spent": "• Gut investiertes Geld",

    // Savings Section - Price Drop Alerts
    "savings.average": "Durchschnittliche Ersparnis bei verfolgten Artikeln",
    "savings.time": "Durchschnittliche Zeit von Alarm bis Kauf",
    "savings.users": "Nutzer, die innerhalb von 24 Stunden kaufen",

    // Coming Soon Badge
    "badge.coming.soon": "Bald verfügbar",

    // Home page additional translations
    "home.hero.badge.full": "Intelligente Geschenkverwaltungsplattform",
    "home.hero.badge.short": "Intelligentes Schenken",
    "home.hero.button.instant": "Sofortige Wunschliste • Ohne Anmeldung",
    "home.hero.button.public.Randomizer": "Wichteln spielen • Ohne Anmeldung",
    "home.hero.appstore.cta": "Im App Store laden",
    "home.hero.appstore.badge.prefix": "Laden im",
    "home.hero.googleplay.cta": "Jetzt bei Google Play",
    "home.hero.googleplay.badge.prefix": "Jetzt bei",
    "home.trust.secure": "Sicher & Privat",
    "home.trust.free": "Kostenloser Start",
    "home.trust.social": "Soziale Funktionen",
    "home.features.subtitle": "Alles, was Sie brauchen, um das Schenken mühelos und unvergesslich zu machen",
    "home.features.learnmore": "Mehr erfahren →",
    "home.cta.badge": "Schließen Sie sich Tausenden zufriedener Nutzer an",
    "home.cta.button.try": "Ohne Konto ausprobieren",

    // Toast messages
    "toast.copied.title": "Kopiert!",
    "toast.copied.description": "Benutzername in Zwischenablage kopiert",
    "toast.copyfailed.title": "Kopieren fehlgeschlagen",
    "toast.copyfailed.description": "Benutzername konnte nicht in Zwischenablage kopiert werden",

    // Profile section
    "profile.userdetails": "Benutzerdetails",
    "profile.fullname": "Vollständiger Name",
    "profile.nickname": "Benutzername",
    "profile.phone": "Telefonnummer",
    "profile.birthday": "Geburtstag",
    "profile.notset": "Nicht festgelegt",
    "profile.identity": "Identität",
    "profile.bio": "Biografie",
    "profile.nobio": "Noch keine Biografie hinzugefügt",
    "profile.gender": "Geschlecht",
    "profile.social": "Soziale Netzwerke",
    "profile.instagram": "Instagram",
    "profile.facebook": "Facebook",
    "profile.edit": "Bearbeiten",
    "profile.edit.name": "Name bearbeiten",
    "profile.edit.nickname": "Benutzername bearbeiten",
    "profile.edit.phone": "Telefonnummer bearbeiten",
    "profile.edit.birthday": "Geburtstag bearbeiten",
    "profile.edit.bio": "Biografie bearbeiten",
    "profile.edit.gender": "Geschlecht bearbeiten",
    "profile.edit.instagram": "Instagram bearbeiten",
    "profile.edit.facebook": "Facebook bearbeiten",
    "profile.avatar.change": "Ihr Avatar ändern",

    // Occasions - Smart Recommendations
    "occasions.birthdays": "🎂 Geburtstage",
    "occasions.birthdays.desc": "Erhalte altersgerechte Empfehlungen, die zu ihren aktuellen Interessen und Hobbys passen.",
    "occasions.birthdays.age": "• Berücksichtigt Alter und Lebensphase",
    "occasions.birthdays.interests": "• Passt zu aktuellen Interessen",
    "occasions.birthdays.balance": "• Balanciert praktische und spaßige Geschenke",
    "occasions.holidays": "🎄 Feiertage",
    "occasions.holidays.desc": "Saisonale Empfehlungen, die den Feiertagsgeist einfangen und dabei persönlich bleiben.",
    "occasions.holidays.themed": "• Feiertagsthematische Vorschläge",
    "occasions.holidays.family": "• Familienfreundliche Optionen",
    "occasions.holidays.trends": "• Saisonale Trends enthalten",
    "occasions.anniversaries": "💍 Jahrestage",
    "occasions.anniversaries.desc": "Romantische und bedeutungsvolle Vorschläge, die eure einzigartige Beziehung feiern.",
    "occasions.anniversaries.milestone": "• Beziehungsmeilenstein bewusst",
    "occasions.anniversaries.romantic": "• Romantisch und durchdacht",
    "occasions.anniversaries.memory": "• Auf Erinnerungsbildung fokussiert",
    "occasions.graduations": "🎓 Abschlüsse",
    "occasions.graduations.desc": "Erfolg feiernde Geschenke, die ihr nächstes Lebenskapitel unterstützen.",
    "occasions.graduations.career": "• Karrierephase angemessen",
    "occasions.graduations.future": "• Zukunftsorientierte Vorschläge",
    "occasions.graduations.achievement": "• Erfolg feiern",

    // Chrome Extension - German
    "chrome.extension.badge": "Jetzt für Chrome verfügbar",
    "chrome.extension.hero.title": "Artikel von überall zu Ihrer Wunschliste hinzufügen",
    "chrome.extension.hero.subtitle": "Installieren Sie unsere Chrome-Erweiterung und fügen Sie Produkte mit nur einem Klick zu Ihrer BuyForS-Wunschliste von jeder Website hinzu. Erkennt automatisch Preise, Bilder und Details.",
    "chrome.extension.install": "Erweiterung installieren",
    "chrome.extension.createaccount": "Kostenloses Konto erstellen",
    "chrome.extension.howitworks.title": "So funktioniert es",
    "chrome.extension.howitworks.step1.title": "Erweiterung installieren",
    "chrome.extension.howitworks.step1.desc": "Fügen Sie die BuyForS-Erweiterung mit einem Klick zu Chrome hinzu. Melden Sie sich mit Ihrem BuyForS-Konto an oder erstellen Sie ein neues.",
    "chrome.extension.howitworks.step2.title": "Jeden Shop durchsuchen",
    "chrome.extension.howitworks.step2.desc": "Kaufen Sie bei Amazon, eBay, Etsy oder jedem Online-Shop ein. Finden Sie Produkte, die Sie überall im Web lieben.",
    "chrome.extension.howitworks.step3.title": "Klicken & Speichern",
    "chrome.extension.howitworks.step3.desc": "Klicken Sie auf das Erweiterungssymbol, um den Artikel sofort hinzuzufügen. Produktdetails, Preis und Bild werden automatisch ausgefüllt.",
    "chrome.extension.features.title": "Leistungsstarke Funktionen",
    "chrome.extension.features.subtitle": "Alles, was Sie brauchen, um Ihre Wunschliste im gesamten Web zu verwalten",
    "chrome.extension.feature.autodetect.title": "Automatische Erkennung",
    "chrome.extension.feature.autodetect.desc": "Extrahiert automatisch Produktname, Beschreibung, Preis und Bilder von jeder Produktseite.",
    "chrome.extension.feature.universal.title": "Universelle Kompatibilität",
    "chrome.extension.feature.universal.desc": "Funktioniert auf Amazon, eBay, Etsy, Walmart, Target und tausenden anderen Online-Shops.",
    "chrome.extension.feature.pricetrack.title": "Preisverfolgung",
    "chrome.extension.feature.pricetrack.desc": "Aktivieren Sie Preisfall-Benachrichtigungen für jeden Artikel und werden Sie benachrichtigt, wenn die Preise fallen.",
    "chrome.extension.feature.privacy.title": "Datenschutzkontrollen",
    "chrome.extension.feature.privacy.desc": "Wählen Sie, wer Ihre Artikel sieht: öffentlich, privat oder geheim. Volle Kontrolle über Ihre Wunschliste.",
    "chrome.extension.feature.sections.title": "Organisierte Abschnitte",
    "chrome.extension.feature.sections.desc": "Fügen Sie Artikel direkt zu bestimmten Wunschlistenabschnitten hinzu. Halten Sie alles automatisch organisiert.",
    "chrome.extension.feature.notify.title": "Freundesbenachrichtigungen",
    "chrome.extension.feature.notify.desc": "Benachrichtigen Sie Freunde, wenn Sie neue Artikel hinzufügen, die sie Ihnen schenken möchten.",
    "chrome.extension.security.title": "Datenschutz & Sicherheit zuerst",
    "chrome.extension.security.subtitle": "Ihre Daten sind verschlüsselt und geschützt. Wir verfolgen niemals Ihre Browsing-Aktivität.",
    "chrome.extension.security.auth.title": "Sichere Authentifizierung",
    "chrome.extension.security.auth.desc": "Melden Sie sich sicher mit Google SSO oder E-Mail an. Ihre Anmeldedaten sind mit branchenüblicher Verschlüsselung geschützt.",
    "chrome.extension.security.notrack.title": "Kein Tracking",
    "chrome.extension.security.notrack.desc": "Wir greifen nur auf Seitendaten zu, wenn Sie auf die Erweiterung klicken. Ihr Browserverlauf bleibt privat.",
    "chrome.extension.security.permissions.title": "Minimale Berechtigungen",
    "chrome.extension.security.permissions.desc": "Wir fordern nur die Berechtigungen an, die für die Funktion erforderlich sind. Kein unnötiger Zugriff auf Ihre Daten.",
    "chrome.extension.security.encrypted.title": "Verschlüsselte Speicherung",
    "chrome.extension.security.encrypted.desc": "Alle Ihre Daten sind verschlüsselt und sicher gespeichert. Sie kontrollieren, wer was sieht.",
    "chrome.extension.cta.badge": "Jetzt verfügbar",
    "chrome.extension.cta.title": "Beginnen Sie noch heute mit dem Aufbau Ihrer Wunschliste",
    "chrome.extension.cta.subtitle": "Schließen Sie sich Tausenden von Nutzern an, die mit BuyForS intelligenter einkaufen. Installieren Sie die Erweiterung und beginnen Sie in Sekunden mit dem Hinzufügen von Artikeln.",
    "chrome.extension.cta.install": "Kostenlose Erweiterung installieren",
    "chrome.extension.cta.account": "Konto erstellen",
    "chrome.extension.cta.note": "Für immer kostenlos · Keine Kreditkarte erforderlich · Funktioniert auf allen großen Websites",

    // Registration form fields and age validation - German
    "register.form.email": "E-Mail",
    "register.form.email.placeholder": "E-Mail eingeben",
    "register.form.fullName": "Vollständiger Name",
    "register.form.fullName.placeholder": "Ihren vollständigen Namen eingeben",
    "register.form.birthday": "Geburtsdatum",
    "register.form.birthday.placeholder": "JJJJ-MM-TT",
    "register.form.nickname": "Benutzername",
    "register.form.nickname.placeholder": "Benutzername eingeben",
    "register.form.password": "Passwort",
    "register.form.password.placeholder": "Passwort eingeben",
    "register.form.phone": "Telefonnummer (optional)",
    "register.form.phone.placeholder": "Telefonnummer eingeben",
    "register.form.eligibility": "Sie müssen mindestens 16 Jahre alt sein, um sich zu registrieren",
    "register.form.terms": "Mit der Registrierung stimmen Sie unseren",
    "register.form.termsLink": "Nutzungsbedingungen",
    "register.form.and": "und",
    "register.form.privacyLink": "Datenschutzrichtlinie",
    "register.form.aiConsent": ", einschließlich der Nutzung von KI-gestützten Funktionen (Google Gemini) für Geschenkempfehlungen",
    "register.button.register": "Registrieren",
    "register.button.registering": "Registrierung läuft...",

    // Zod-Schema-Validierungsmeldungen (RegisterForm)
    "register.form.error.email": "Ungültiges E-Mail-Format",
    "register.form.error.password": "Das Passwort muss mindestens 9 Zeichen lang sein",
    "register.form.error.fullName": "Vollständiger Name ist erforderlich",
    "register.form.error.birthdayRequired": "Geburtsdatum ist erforderlich",
    "register.form.error.age16": "Sie müssen mindestens 16 Jahre alt sein, um BuyForS zu nutzen. Gemäß DSGVO (Art. 8) und deutschem Recht erfordert die Verarbeitung personenbezogener Daten von Kindern unter 16 Jahren die Zustimmung der Eltern. Bitte bitten Sie einen Elternteil oder Erziehungsberechtigten, sich in Ihrem Namen zu registrieren.",
    "register.form.error.termsAccepted": "Sie müssen den Nutzungsbedingungen und der Datenschutzrichtlinie zustimmen",
    "register.form.error.generic": "Bei der Registrierung ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut.",

    // Age validation errors - German
    "errors.age.tooYoung": "Sie müssen mindestens 16 Jahre alt sein, um sich zu registrieren",
    "errors.age.tooOld": "Bitte geben Sie ein gültiges Geburtsdatum ein",
    "chrome.extension.benefit.free": "Für immer kostenlos",
    "chrome.extension.benefit.works": "Funktioniert überall",
    "chrome.extension.benefit.secure": "Sicher & privat",
    "home.feature.chrome.title": "Chrome-Erweiterung",
    "home.feature.chrome.description": "Artikel von jeder Website mit einem Klick hinzufügen",
    "home.feature.dailyquiz.title": "Tägliches Mini-Quiz",
    "home.feature.dailyquiz.description": "Beantworte jeden Tag ein paar lustige Fragen und finde heraus, was für ein Planer du bist. Keine Anmeldung erforderlich.",
    "home.feature.dailyquiz.cta": "Jetzt spielen",
    "home.feature.friendquiz.title": "Freunde-Quiz",
    "home.feature.friendquiz.description": "Erstelle lustige Quizze über dich und teile sie mit Freunden",
    "home.feature.friendquiz.cta": "Mehr erfahren",
    "home.feature.mafia.title": "Mafia Spiel",
    "home.feature.mafia.description": "Spiele das klassische Gesellschaftsspiel mit Freunden. Entlarve die Mafia oder täusche dich zum Sieg!",
    "home.feature.sync.title": "Sync Spiel",
    "home.feature.sync.description": "Denke wie deine Freunde, aber nicht wie alle. Finde 2-3 Personen für maximale Punkte!",
    "mafia.landing.badge": "Mafia Rollenzuweisung",
    "mafia.landing.hero.title": "Mafia-Rollen sofort zuweisen",
    "mafia.landing.hero.subtitle": "Der einfachste Weg, zufällige Rollen für euer Mafia-Partyspiel zuzuweisen. Spieler hinzufügen, Rollen wählen, und alle werden geheim benachrichtigt!",
    "mafia.landing.cta.create": "Spiel erstellen",
    "mafia.landing.cta.signup": "Kostenlos starten",
    "mafia.landing.cta.account": "Konto erstellen",
    "mafia.landing.benefit.players": "6+ Spieler",
    "mafia.landing.benefit.roles": "8 Verschiedene Rollen",
    "mafia.landing.benefit.invites": "Sofortige Benachrichtigungen",
    "mafia.landing.howitworks.title": "So funktioniert's",
    "mafia.landing.howitworks.step1.title": "1. Spieler hinzufügen",
    "mafia.landing.howitworks.step1.desc": "Füge mindestens 6 Spieler aus deiner Freundesliste oder per E-Mail hinzu. Sie brauchen kein Konto!",
    "mafia.landing.howitworks.step2.title": "2. Spiel starten",
    "mafia.landing.howitworks.step2.desc": "Wähle welche Rollen dabei sein sollen und klicke Start. Rollen werden sofort zufällig zugewiesen.",
    "mafia.landing.howitworks.step3.title": "3. Spielen!",
    "mafia.landing.howitworks.step3.desc": "Alle erhalten ihre geheime Rolle per E-Mail und Benachrichtigung. Jetzt persönlich mit Freunden spielen!",
    "mafia.landing.roles.title": "Verfügbare Rollen",
    "mafia.landing.roles.subtitle": "Wähle welche Rollen im Spiel sein sollen. Jeder Spieler bekommt eine Rolle zufällig zugewiesen.",
    "mafia.landing.role.mafia.name": "Mafia",
    "mafia.landing.role.mafia.desc": "Die Bösen. Sie kennen sich und versuchen die Stadt zu eliminieren.",
    "mafia.landing.role.godfather.name": "Pate",
    "mafia.landing.role.godfather.desc": "Anführer der Mafia. Erscheint bei Untersuchungen als unschuldig.",
    "mafia.landing.role.doctor.name": "Arzt",
    "mafia.landing.role.doctor.desc": "Kann jede Nacht einen Spieler vor Eliminierung schützen.",
    "mafia.landing.role.detective.name": "Detektiv",
    "mafia.landing.role.detective.desc": "Kann jede Nacht einen Spieler untersuchen um sein Team zu erfahren.",
    "mafia.landing.role.citizen.name": "Bürger",
    "mafia.landing.role.citizen.desc": "Normaler Stadtbewohner ohne besondere Fähigkeiten.",
    "mafia.landing.role.vigilante.name": "Bürgerwehr",
    "mafia.landing.role.vigilante.desc": "Stadt-Spieler der nachts jemanden eliminieren kann.",
    "mafia.landing.role.escort.name": "Eskorte",
    "mafia.landing.role.escort.desc": "Kann jede Nacht die Aktion eines Spielers blockieren.",
    "mafia.landing.role.bodyguard.name": "Leibwächter",
    "mafia.landing.role.bodyguard.desc": "Kann einen Spieler schützen und den Treffer abfangen.",
    "mafia.landing.team.mafia": "Mafia",
    "mafia.landing.team.town": "Stadt",
    "mafia.landing.features.title": "Funktionen",
    "mafia.landing.features.subtitle": "Alles was du für einen Mafia-Spieleabend brauchst",
    "mafia.landing.feature.notifications.title": "Sofortige Benachrichtigungen",
    "mafia.landing.feature.notifications.desc": "Beim Start erhalten alle Spieler sofort ihre Rolle per E-Mail. Registrierte Benutzer auch per In-App-Benachrichtigung.",
    "mafia.landing.feature.invite.title": "Jeden hinzufügen",
    "mafia.landing.feature.invite.desc": "Füge Freunde aus deiner Liste oder jeden per E-Mail hinzu. Sie erhalten ihre Rolle ohne Konto.",
    "mafia.landing.feature.roles.title": "Anpassbare Rollen",
    "mafia.landing.feature.roles.desc": "Wähle genau welche Rollen im Spiel erscheinen. Erstelle die perfekte Balance für eure Gruppengröße.",
    "mafia.landing.feature.secret.title": "Geheime Zuweisung",
    "mafia.landing.feature.secret.desc": "Jeder Spieler erhält seine Rolle privat. Nur er kennt seine Identität - perfekt für persönliche Spiele!",
    "mafia.landing.final.badge": "Bereit zu spielen?",
    "mafia.landing.final.title": "Erstelle jetzt dein Spiel",
    "mafia.landing.final.subtitle": "Füge deine Freunde hinzu, wähle die Rollen und starte die Zuweisung. Dauert nur eine Minute!",
    "mafia.landing.final.cta": "Jetzt Spiel erstellen",
    "mafia.landing.final.note": "Kostenlos • Kein Download erforderlich • Funktioniert auf jedem Gerät",
    "friendquiz.landing.badge": "Freunde-Quiz",
    "friendquiz.landing.hero.title": "Wie gut kennen dich deine Freunde?",
    "friendquiz.landing.hero.subtitle": "Erstelle lustige Quizze über dich, teile sie mit Freunden und finde heraus, wer dich am besten kennt!",
    "friendquiz.landing.cta.create": "Quiz erstellen",
    "friendquiz.landing.cta.signup": "Kostenlos starten",
    "friendquiz.landing.cta.account": "Konto erstellen",
    "friendquiz.landing.benefit.free": "100% Kostenlos",
    "friendquiz.landing.benefit.private": "Nur für Freunde",
    "friendquiz.landing.benefit.instant": "Sofortige Ergebnisse",
    "friendquiz.landing.howitworks.title": "So funktioniert's",
    "friendquiz.landing.howitworks.step1.title": "Quiz erstellen",
    "friendquiz.landing.howitworks.step1.desc": "Wähle aus Vorlagenfragen oder erstelle eigene. Gestalte ein Quiz, das dich widerspiegelt.",
    "friendquiz.landing.howitworks.step2.title": "Freunde einladen",
    "friendquiz.landing.howitworks.step2.desc": "Teile dein Quiz mit Freunden. Sie werden benachrichtigt und können das Quiz jederzeit machen.",
    "friendquiz.landing.howitworks.step3.title": "Ergebnisse sehen",
    "friendquiz.landing.howitworks.step3.desc": "Sieh, wer dein Quiz gemacht hat und wie gut sie dich kennen. Vergleiche Punktzahlen!",
    "friendquiz.landing.features.title": "Funktionen",
    "friendquiz.landing.features.subtitle": "Alles was du brauchst, um spannende Quizze für deine Freunde zu erstellen",
    "friendquiz.landing.feature.templates.title": "Vorlagenfragen",
    "friendquiz.landing.feature.templates.desc": "Starte schnell mit vorgefertigten Fragenvorlagen aus verschiedenen Kategorien.",
    "friendquiz.landing.feature.friendsonly.title": "Nur für Freunde",
    "friendquiz.landing.feature.friendsonly.desc": "Deine Quizze sind privat. Nur deine Freunde auf BuyForS können sie machen.",
    "friendquiz.landing.feature.notifications.title": "Sofortige Benachrichtigungen",
    "friendquiz.landing.feature.notifications.desc": "Freunde werden benachrichtigt, wenn du ein Quiz erstellst, und du wirst benachrichtigt, wenn sie es abschließen.",
    "friendquiz.landing.feature.scores.title": "Punkteverfolgung",
    "friendquiz.landing.feature.scores.desc": "Sieh detaillierte Ergebnisse mit Punktzahlen, Prozenten und einzelnen Antwortaufschlüsselungen.",
    "friendquiz.landing.feature.sharing.title": "Einfaches Teilen",
    "friendquiz.landing.feature.sharing.desc": "Teile deinen Quiz-Link mit Freunden oder lade sie direkt von der Plattform ein.",
    "friendquiz.landing.feature.privacy.title": "Privat & Sicher",
    "friendquiz.landing.feature.privacy.desc": "Deine Quiz-Daten sind privat. Nur du kannst sehen, wer geantwortet hat und wie.",
    "friendquiz.landing.privacy.title": "Deine Privatsphäre ist wichtig",
    "friendquiz.landing.privacy.subtitle": "Freunde-Quiz wurde mit Blick auf deine Privatsphäre entwickelt",
    "friendquiz.landing.privacy.friendsonly.title": "Nur Freunde haben Zugang",
    "friendquiz.landing.privacy.friendsonly.desc": "Nur Personen, die du als Freunde hinzugefügt hast, können deine Quizze sehen und machen.",
    "friendquiz.landing.privacy.results.title": "Private Ergebnisse",
    "friendquiz.landing.privacy.results.desc": "Nur du kannst sehen, wer dein Quiz gemacht hat und wie sie geantwortet haben.",
    "friendquiz.landing.privacy.nopublic.title": "Kein öffentliches Teilen",
    "friendquiz.landing.privacy.nopublic.desc": "Quizze können nicht öffentlich geteilt werden - sie bleiben in deinem Freundeskreis.",
    "friendquiz.landing.privacy.delete.title": "Jederzeit löschen",
    "friendquiz.landing.privacy.delete.desc": "Du kannst dein Quiz und alle Antworten jederzeit löschen.",
    "friendquiz.landing.final.badge": "Heute starten",
    "friendquiz.landing.final.title": "Bereit herauszufinden, wer dich am besten kennt?",
    "friendquiz.landing.final.subtitle": "Erstelle noch heute dein erstes Freunde-Quiz und teile es mit deinem Kreis.",
    "friendquiz.landing.final.cta": "Quiz jetzt erstellen",
    "friendquiz.landing.final.note": "Für immer kostenlos • Keine Kreditkarte erforderlich",
    "sync.landing.badge": "Denke wie deine Freunde",
    "sync.landing.hero.title": "SYNC Spiel",
    "sync.landing.hero.subtitle": "Eine Frage erscheint mit 6 Optionen. Alle wählen eine. Finde 2-3 Personen für maximale Punkte. Zu offensichtlich? Null Punkte. Zu einzigartig? Auch null. Finde deine Gedankenzwillinge!",
    "sync.landing.cta.create": "Spiel erstellen",
    "sync.landing.cta.signup": "Kostenlos starten",
    "sync.landing.cta.account": "Konto erstellen",
    "sync.landing.benefit.players": "3+ Spieler",
    "sync.landing.benefit.rounds": "5-15 Runden",
    "sync.landing.benefit.realtime": "Echtzeit-Gameplay",
    "sync.landing.howitworks.title": "So funktioniert's",
    "sync.landing.howitworks.step1.title": "1. Erstellen & Einladen",
    "sync.landing.howitworks.step1.desc": "Erstelle einen Spielraum und lade 3+ Freunde ein. Wähle deine Runden und Zeit.",
    "sync.landing.howitworks.step2.title": "2. Wähle deine Antwort",
    "sync.landing.howitworks.step2.desc": "Eine Frage erscheint mit 6 Optionen. Alle wählen geheim eine. Finde 2-3 Personen, die wie du denken!",
    "sync.landing.howitworks.step3.title": "3. Punkten & Gewinnen",
    "sync.landing.howitworks.step3.desc": "Antworten werden aufgedeckt. Finde 2-3 Personen = viele Punkte. Zu offensichtlich oder zu einzigartig = 0 Punkte.",
    "sync.landing.scoring.title": "Punktesystem",
    "sync.landing.scoring.subtitle": "Die perfekte Balance: finde ein paar, nicht alle",
    "sync.landing.scoring.solo": "Zu einzigartig",
    "sync.landing.scoring.pair": "Perfekter Punkt",
    "sync.landing.scoring.trio": "Gute Balance",
    "sync.landing.scoring.quad": "Wird offensichtlich",
    "sync.landing.scoring.hivemind": "Gruppendenken",
    "sync.landing.features.title": "Funktionen",
    "sync.landing.features.subtitle": "Alles was du für das perfekte Partyspiel brauchst",
    "sync.landing.feature.quick.title": "Schnelle Runden",
    "sync.landing.feature.quick.desc": "15-Sekunden-Runden halten das Spiel schnell und aufregend",
    "sync.landing.feature.ai.title": "KI-Fragen",
    "sync.landing.feature.ai.desc": "KI-generierte Fragen mit 6 lustigen Optionen. Spiele nie das gleiche Spiel zweimal",
    "sync.landing.feature.mind.title": "Gedankenzwillinge",
    "sync.landing.feature.mind.desc": "Entdecke wer wie du denkt mit besonderen Auszeichnungen und Match-Tracking",
    "sync.landing.feature.invite.title": "Einfache Einladungen",
    "sync.landing.feature.invite.desc": "Teile einen Link und Freunde können sofort beitreten. Keine komplizierte Einrichtung",
    "sync.landing.final.badge": "Perfekt für Wochenendnächte",
    "sync.landing.final.title": "Bereit deine Gedankenzwillinge zu finden?",
    "sync.landing.final.subtitle": "Erstelle ein Spiel, lade Freunde ein und entdecke wer wie du denkt. Wähle aus 6 Optionen, sofortige Enthüllungen und endloser Spaß.",
    "sync.landing.final.cta": "Spiel erstellen",
    "sync.landing.final.note": "Kostenlos • Kein Download erforderlich • Funktioniert auf jedem Gerät"
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem("language") as Language;
    if (savedLanguage && (savedLanguage === "en" || savedLanguage === "de")) {
      return savedLanguage;
    }
    const browserLang = navigator.language.substring(0, 2);
    return browserLang === "de" ? "de" : "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const translate = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translate }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
